import React, {
  memo,
  useEffect,
  useRef,
  useState,
  useCallback,
  useMemo,
  Fragment,
} from "react";
import { createPortal } from "react-dom";
import { DateTimeInputPopover } from "./DateTimePicker";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQuery, useCommand, useStore, useRpc } from "../store";
import { useColors, useStyles } from "../ui";
import { useTheme } from "../theme";
import { useI18n } from "../i18n";
import { useAuth } from "../auth";
import { useAnon } from "../anon";
import { db, getSqlocal } from "../db";
import * as s from "../../../shared/src/schema";
import { eq, inArray, like } from "drizzle-orm";
import type { CommandType } from "../../../shared/src/commands";
import type { FieldOverlay } from "../pages/case/formFields";
import {
  GROUP_COLOURS,
  HARDCODED_BOUNDS,
  SECTION_TO_GROUP,
  getFieldHelpContent,
  getHelpSection,
} from "../pages/case/fieldHelp";
import type {
  PatientListItem,
  PatientListResult,
} from "../../../shared/src/protocol";
import { uuidv7 } from "../../../shared/src/ids";
import { IconTick } from "../icons";
import { BioSpinner } from "./BioSpinner";
import { AttachmentsSection } from "../pages/case/AttachmentsSection";
import { storeBlob, getBlob } from "../blobs";
import {
  optBool as _bool,
  str as _str,
} from "../../../shared/src/projectionHelpers";
import {
  BodyDiagramPopover,
  CrossMark,
  PDF_ZONE_POSITIONS,
  TRAUMA_CROSS_POSITIONS,
} from "../pages/body-diagram";
import { exportPdf } from "../exportPdf";
import type { TraumaMap } from "../../../shared/src/commands";

import page1Svg from "../assets/001page1.svg?raw";
import page2Svg from "../assets/001page2.svg?raw";
import page1DarkSvg from "../assets/001page1dark.svg?raw";
import page2DarkSvg from "../assets/001page2dark.svg?raw";

// SVG viewBox dimensions (matches the original PDF exactly)
const SVG_W = 595.28;
const SVG_H = 841.89;
const ASPECT = SVG_H / SVG_W;

/** Keep --vvh in sync with the visual viewport so popovers dodge the virtual keyboard. */
if (typeof window !== "undefined" && window.visualViewport) {
  const update = () =>
    document.documentElement.style.setProperty(
      "--vvh",
      `${window.visualViewport!.height}px`,
    );
  update();
  window.visualViewport.addEventListener("resize", update);
}

/** Shared backdrop style for all modal popovers — centered, constrained to visible viewport. */
export const POPOVER_BACKDROP: React.CSSProperties = {
  position: "fixed",
  top: 0,
  left: 0,
  right: 0,
  height: "var(--vvh, 100dvh)",
  zIndex: 1000,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  background: "rgba(0,0,0,0.25)",
};

/** Shared button style for popover footers. */
export function popoverBtnStyle(colors: Record<string, string>): React.CSSProperties {
  return {
    padding: "0.4em 1em",
    border: `1px solid ${colors.border}`,
    borderRadius: "0.25em",
    background: "transparent",
    cursor: "pointer",
    fontFamily: "'e-Ukraine', system-ui, sans-serif",
    fontSize: "0.85em",
  };
}

/**
 * Consistent footer for all popovers: [— clear] [optional children] <spacer> [✓ commit]
 */
export function PopoverFooter({
  onClear,
  onCommit,
  colors,
  children,
  noBorder,
}: {
  onClear: () => void;
  onCommit: () => void;
  colors: Record<string, string>;
  children?: React.ReactNode;
  noBorder?: boolean;
}) {
  const btn = popoverBtnStyle(colors);
  return (
    <div
      style={{
        padding: "0.5em 0.75em",
        ...(noBorder ? { paddingTop: 0 } : { borderTop: `1px solid ${colors.border}` }),
        display: "flex",
        alignItems: "center",
        gap: "0.5em",
      }}
    >
      <button onClick={onClear} style={{ ...btn, color: colors.textMuted }}>
        —
      </button>
      {children}
      <span style={{ flex: 1 }} />
      <button
        onClick={onCommit}
        style={{ ...btn, background: colors.bg, color: colors.formInk, fontWeight: 600 }}
      >
        {IconTick}
      </button>
    </div>
  );
}

// ─── History helpers ──────────────────────────────────────────────────

interface HistoryEntry {
  id: string;
  type: string;
  payload: Record<string, unknown>;
  userId: string;
  clientTimestamp: number;
  serverTimestamp: number;
}

/** Human-readable label for a command type */
function commandLabel(type: string): string {
  const parts = type.split(".");
  const entity = parts[0];
  const action = parts[1] || "";

  if (action === "create") return `+ ${entity}`;
  if (action === "delete") return `\u2212 ${entity}`;
  if (action === "archive") return `\u{1F4E6} ${entity}`;

  // Remove 'set' prefix, split camelCase into readable form
  const field = action.replace(/^set/, "");
  const readable = field
    .replace(/([A-Z])/g, " $1")
    .replace(/^R2 /, "R2: ")
    .replace(/^R1 /, "R1: ")
    .trim();

  return readable || action;
}

/** Format a single value for display */
function fmtVal(v: unknown): string {
  if (v === null || v === undefined) return "\u2014";
  if (typeof v === "boolean") return v ? "\u2713" : "\u2717";
  if (typeof v === "number") {
    if (v > 1e12)
      return new Date(v).toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      });
    return String(v);
  }
  if (Array.isArray(v)) {
    if (v.length === 0) return "\u2014";
    return v
      .map((x) => (typeof x === "string" ? x : JSON.stringify(x)))
      .join(", ");
  }
  if (typeof v === "object") return JSON.stringify(v);
  const s = String(v);
  return s.length > 50 ? s.slice(0, 47) + "\u2026" : s;
}

/** Format a payload for display in history sidebar */
function formatValue(payload: Record<string, unknown>): string {
  // Skip internal IDs
  const skip = new Set(["uuid", "caseUuid", "patientUuid", "hospitalUuid"]);
  const entries = Object.entries(payload).filter(([k]) => !skip.has(k));
  if (entries.length === 0) return "";
  // Single value — show just the value
  if (entries.length === 1) return fmtVal(entries[0][1]);
  return entries.map(([k, v]) => `${k}: ${fmtVal(v)}`).join(", ");
}

/** Format timestamp as HH:MM:SS */
function fmtTime(ts: number): string {
  if (!ts) return "—";
  const d = new Date(ts);
  return d.toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
}

/** Format date as DD.MM.YYYY */
function fmtDate(ts: number): string {
  if (!ts) return "";
  const d = new Date(ts);
  return `${String(d.getDate()).padStart(2, "0")}.${String(d.getMonth() + 1).padStart(2, "0")}.${d.getFullYear()}`;
}

// ─── History replay: rebuild data snapshot from events ───────────────

type P = Record<string, unknown>;

/**
 * Replay a sorted (ASC) list of events up to cutoffIndex (inclusive)
 * and produce a data object with the same shape the overlay system expects.
 */
function replayEvents(
  events: HistoryEntry[],
  cutoffIndex: number,
  hospitals: unknown[],
  units?: Array<{ id: string; name: string | null }>,
): Record<string, any> {
  const caseObj: P = {};
  const patientObj: P = {};
  // Keyed child entities: id -> fields
  const childRows: Record<string, Record<string, P>> = {
    vitals: {},
    ventilation: {},
    diagnostics: {},
    labValues: {},
    exam: {},
    cpr: {},
    tourniquet: {},
    medication: {},
    surgery: {},
  };
  const attachmentRows: Record<string, P> = {};
  const deletedChildren = new Set<string>();

  for (let i = 0; i <= cutoffIndex && i < events.length; i++) {
    const evt = events[i];
    const p = evt.payload;
    const prefix = evt.type.split(".")[0];
    const action = evt.type.split(".")[1] || "";
    const uuid = p.uuid as string;

    if (prefix === "case") {
      if (action === "create") {
        caseObj.id = uuid;
      } else if (action === "setPatient") {
        caseObj.patientId = p.patientUuid;
      } else if (action === "setNumber") {
        caseObj.number = p.number;
      } else if (action === "setHospital") {
        caseObj.hospitalId = p.hospitalUuid;
      } else if (action === "setEventTime") {
        caseObj.eventTime = p.timestamp;
      } else if (action === "setEventLocation") {
        caseObj.eventLocation = p.location;
      } else if (action === "setArrivalTime") {
        caseObj.arrivalTime = p.timestamp;
      } else if (action === "setTriageCategory") {
        caseObj.triageCategory = p.triageCategory;
      } else if (action === "setRole") {
        caseObj.role = _str(p.role);
      } else if (action === "setMedicalFacility") {
        caseObj.medicalFacility = p.facility;
      } else if (action === "setTransportType") {
        const v = p.transportType as string;
        caseObj.transportType = v ? [v] : [];
      } else if (action === "setTransportTypeItem") {
        const field = p.field as string;
        const key = field === "discharge" ? "dischargeTransportType"
          : field === "r2Discharge" ? "r2DischargeTransportType"
          : "transportType";
        const a = (caseObj[key] as string[]) || [];
        caseObj[key] = p.value
          ? a.includes(p.key as string) ? a : [...a, p.key as string]
          : a.filter((x: string) => x !== p.key);
      } else if (action === "setTraumaTypeItem") {
        const a = (caseObj.traumaTypes as string[]) || [];
        caseObj.traumaTypes = p.value
          ? a.includes(p.key as string)
            ? a
            : [...a, p.key as string]
          : a.filter((x: string) => x !== p.key);
      } else if (action === "setTraumaPenetrating") {
        caseObj.traumaPenetrating = _bool(p.penetrating);
      } else if (action === "setTraumaBurnItem") {
        const tl = (caseObj.traumaLocations as any) || {
          burns: [],
          traumas: [],
          injuries: [],
          arrows: [],
        };
        const a = tl.burns || [];
        caseObj.traumaLocations = {
          ...tl,
          burns: p.value
            ? a.includes(p.zone)
              ? a
              : [...a, p.zone]
            : a.filter((z: string) => z !== p.zone),
        };
      } else if (action === "setTraumaZoneItem") {
        const tl = (caseObj.traumaLocations as any) || {
          burns: [],
          traumas: [],
          injuries: [],
          arrows: [],
        };
        const a = tl.traumas || [];
        caseObj.traumaLocations = {
          ...tl,
          traumas: p.value
            ? a.includes(p.zone)
              ? a
              : [...a, p.zone]
            : a.filter((z: string) => z !== p.zone),
        };
      } else if (action === "addInjuryMark") {
        const tl = (caseObj.traumaLocations as any) || {
          burns: [],
          traumas: [],
          injuries: [],
          arrows: [],
        };
        caseObj.traumaLocations = {
          ...tl,
          injuries: [...(tl.injuries || []), { id: p.markId, x: p.x, y: p.y }],
        };
      } else if (action === "removeInjuryMark") {
        const tl = (caseObj.traumaLocations as any) || {
          burns: [],
          traumas: [],
          injuries: [],
          arrows: [],
        };
        caseObj.traumaLocations = {
          ...tl,
          injuries: (tl.injuries || []).filter((m: any) => m.id !== p.markId),
        };
      } else if (action === "setConditionTypeItem") {
        const a = (caseObj.conditionTypes as string[]) || [];
        caseObj.conditionTypes = p.value
          ? a.includes(p.key as string)
            ? a
            : [...a, p.key as string]
          : a.filter((x: string) => x !== p.key);
      } else if (action === "setCbrnItem") {
        const a = (caseObj.cbrn as string[]) || [];
        caseObj.cbrn = p.value
          ? a.includes(p.key as string)
            ? a
            : [...a, p.key as string]
          : a.filter((x: string) => x !== p.key);
      } else if (action === "setPriorCareItem") {
        caseObj.priorCare = {
          ...((caseObj.priorCare as Record<string, unknown>) || {}),
          [p.key as string]: p.value,
        };
      } else if (action === "setPriorCareTime") {
        caseObj.priorCareTime = p.timestamp;
      } else if (action === "setTetanusProphylaxis") {
        caseObj.tetanusProphylaxis = p.prophylaxis;
      } else if (action === "setDeathTime") {
        caseObj.deathTime = p.timestamp;
      } else if (action === "setDeathCircumstances") {
        caseObj.deathCircumstances = p.circumstances;
      } else if (action === "setR1CareItem") {
        caseObj.r1Care = {
          ...((caseObj.r1Care as Record<string, boolean | string>) || {}),
          [p.key as string]: p.value,
        };
      } else if (action === "setR1Kit1") {
        caseObj.r1Kit1 = p.kit;
      } else if (action === "setR1Kit2") {
        caseObj.r1Kit2 = p.kit;
      } else if (action === "setDischargeTime") {
        caseObj.dischargeTime = p.timestamp;
      } else if (action === "setDischargeDestination") {
        caseObj.dischargeDestination = p.destination;
      } else if (action === "setEvacuationCategory") {
        caseObj.evacuationCategory = p.evacuationCategory;
      } else if (action === "setDischargeTransportType") {
        const v = p.transportType as string;
        caseObj.dischargeTransportType = v ? [v] : [];
      } else if (action === "setDischargePosition") {
        caseObj.dischargePosition = p.position;
      } else if (action === "setEscortDoctor") {
        caseObj.escortDoctor = _bool(p.escortDoctor);
      } else if (action === "setEscortNurse") {
        caseObj.escortNurse = _bool(p.escortNurse);
      } else if (action === "setDischargeRole") {
        caseObj.dischargeRole = _str(p.role);
      } else if (action === "setDischargeVehicleType") {
        caseObj.dischargeVehicleType = _str(p.vehicleType);
      } else if (action === "setDischargeEquipmentItem") {
        caseObj.dischargeEquipment = {
          ...((caseObj.dischargeEquipment as Record<string, boolean>) || {}),
          [p.key as string]: p.value as boolean,
        };
      } else if (action === "setDiagnosis") {
        caseObj.diagnosis = p.diagnosis;
      } else if (action === "setNotes") {
        caseObj.notes = p.notes;
      } else if (action === "setMedicName") {
        caseObj.medicName = p.name;
      } else if (action === "setMedicFacility") {
        caseObj.medicFacility = p.facility;
      } else if (action === "setMedicRole") {
        caseObj.medicRole = p.role;
      }
      // R2 fields
      else if (action === "setR2ArrivalTime") {
        caseObj.r2ArrivalTime = p.timestamp;
      } else if (action === "setR2TriageCategory") {
        caseObj.r2TriageCategory = p.triageCategory;
      } else if (action === "setR2Hospital") {
        caseObj.r2HospitalId = p.hospitalUuid;
      } else if (action === "setR2CareItem") {
        caseObj.r2Care = {
          ...((caseObj.r2Care as Record<string, boolean | string>) || {}),
          [p.key as string]: p.value,
        };
      } else if (action === "setR2DeathTime") {
        caseObj.r2DeathTime = p.timestamp;
      } else if (action === "setR2DeathCircumstances") {
        caseObj.r2DeathCircumstances = p.circumstances;
      } else if (action === "setR2DischargeTime") {
        caseObj.r2DischargeTime = p.timestamp;
      } else if (action === "setR2DischargeDestination") {
        caseObj.r2DischargeDestination = p.destination;
      } else if (action === "setR2EvacuationCategory") {
        caseObj.r2EvacuationCategory = p.evacuationCategory;
      } else if (action === "setR2DischargeTransportType") {
        const v = p.transportType as string;
        caseObj.r2DischargeTransportType = v ? [v] : [];
      } else if (action === "setR2DischargePosition") {
        caseObj.r2DischargePosition = p.position;
      } else if (action === "setR2EscortDoctor") {
        caseObj.r2EscortDoctor = _bool(p.escortDoctor);
      } else if (action === "setR2EscortNurse") {
        caseObj.r2EscortNurse = _bool(p.escortNurse);
      } else if (action === "setR2DischargeRole") {
        caseObj.r2DischargeRole = _str(p.role);
      } else if (action === "setR2DischargeVehicleType") {
        caseObj.r2DischargeVehicleType = _str(p.vehicleType);
      } else if (action === "setR2DischargeEquipmentItem") {
        caseObj.r2DischargeEquipment = {
          ...((caseObj.r2DischargeEquipment as Record<string, boolean>) || {}),
          [p.key as string]: p.value as boolean,
        };
      } else if (action === "setR1PriorCareStatus") {
        caseObj.r1PriorCareStatus = _str(p.status);
      } else if (action === "setR2Diagnosis") {
        caseObj.r2Diagnosis = p.diagnosis;
      } else if (action === "setR2Notes") {
        caseObj.r2Notes = p.notes;
      } else if (action === "setR2MedicName") {
        caseObj.r2MedicName = p.name;
      } else if (action === "setR2MedicFacility") {
        caseObj.r2MedicFacility = p.facility;
      } else if (action === "setR2MedicRole") {
        caseObj.r2MedicRole = p.role;
      } else if (action === "setR2TetanusProphylaxis") {
        caseObj.r2TetanusProphylaxis = p.prophylaxis;
      } else if (action === "setR2Kit1") {
        caseObj.r2Kit1 = p.kit;
      } else if (action === "setR2Kit2") {
        caseObj.r2Kit2 = p.kit;
      } else if (action === "archive") {
        caseObj.archived = 1;
      } else if (action === "setSignatureData") {
        caseObj.signatureSha256 = p.sha256;
      } else if (action === "setR2SignatureData") {
        caseObj.r2SignatureSha256 = p.sha256;
      }
    } else if (prefix === "patient") {
      if (action === "create") {
        patientObj.id = uuid;
      } else if (action === "setName") {
        patientObj.name = p.name;
      } else if (action === "setDateOfBirth") {
        patientObj.dateOfBirth = p.date;
      } else if (action === "setSex") {
        patientObj.sex = p.sex;
      } else if (action === "setBloodType") {
        patientObj.bloodType = p.bloodType;
      } else if (action === "setRhFactor") {
        patientObj.rhFactor = p.rhFactor;
      } else if (action === "setAllergyStatus") {
        patientObj.allergyStatus = p.status;
      } else if (action === "setAllergyDetails") {
        patientObj.allergyDetails = p.details;
      } else if (action === "setMilitaryRank") {
        patientObj.militaryRank = p.rank;
      } else if (action === "setUnit") {
        patientObj.unitId = p.unitUuid;
        patientObj.unitName = units?.find((u) => u.id === p.unitUuid)?.name || "";
      } else if (action === "setIdNumber") {
        patientObj.idNumber = p.idNumber;
      }
    } else if (prefix === "attachment") {
      if (action === "create") {
        attachmentRows[uuid] = {
          id: uuid,
          mimeType: p.mimeType,
          capturedAt: p.capturedAt,
          sha256: null,
          deleted: 0,
        };
      } else if (action === "setData") {
        if (attachmentRows[uuid]) attachmentRows[uuid].sha256 = p.sha256;
      } else if (action === "delete") {
        if (attachmentRows[uuid]) attachmentRows[uuid].deleted = 1;
      }
    } else if (childRows[prefix] !== undefined) {
      if (action === "create") {
        childRows[prefix][uuid] = {
          id: uuid,
          caseId: p.caseUuid,
          role: p.role ?? null,
        };
        deletedChildren.delete(uuid);
      } else if (action === "delete") {
        deletedChildren.add(uuid);
      } else {
        // All other set commands: merge payload into existing row
        if (!childRows[prefix][uuid]) childRows[prefix][uuid] = { id: uuid };
        const row = childRows[prefix][uuid];
        // Map standard payload keys to row fields
        if (p.timestamp !== undefined) row.time = p.timestamp;
        if (p.value !== undefined) {
          // Determine field from action name: e.g. setRespiratoryRate → respiratoryRate
          const fieldName = action.replace(/^set/, "");
          const key = fieldName.charAt(0).toLowerCase() + fieldName.slice(1);
          row[key] = p.value;
        }
        // Specific fields
        if (p.name !== undefined) row.name = p.name;
        if (p.dose !== undefined) row.dose = p.dose;
        if (p.doseUnit !== undefined) row.doseUnit = p.doseUnit;
        if (p.route !== undefined) row.route = p.route;
        if (p.mode !== undefined) row.mode = p.mode;
        if (p.limb !== undefined) row.limb = p.limb;
        if (p.description !== undefined) row.description = p.description;
        if (p.anesthesiaType !== undefined)
          row.anesthesiaType = p.anesthesiaType;
        if (action === "setSurgeryTypeItem") {
          const a = (row.surgeryTypes as string[]) || [];
          row.surgeryTypes = p.value
            ? a.includes(p.key as string)
              ? a
              : [...a, p.key as string]
            : a.filter((x: string) => x !== p.key);
        }
        if (p.airwayStatus !== undefined) row.airwayStatus = p.airwayStatus;
        if (action === "setBreathingSoundsItem")
          row.breathingSounds = {
            ...((row.breathingSounds as Record<string, boolean>) || {}),
            [p.key as string]: p.value as boolean,
          };
        if (action === "setStartTime") row.startTime = p.timestamp;
        if (action === "setEndTime") row.endTime = p.timestamp;
        if (action === "setApplicationTime") row.applicationTime = p.timestamp;
        if (action === "setRemovalTime") row.removalTime = p.timestamp;
        if (action === "setConversionTime") row.conversionTime = p.timestamp;
        if (action === "setRepositioningTime")
          row.repositioningTime = p.timestamp;
      }
    }
  }

  // Build arrays, filtering out deleted children and sorting by id for consistent order
  const toArray = (key: string) =>
    Object.values(childRows[key])
      .filter((r) => !deletedChildren.has(r.id as string))
      .sort((a, b) => (a.id as string).localeCompare(b.id as string));

  return {
    case: caseObj,
    patient: Object.keys(patientObj).length > 0 ? patientObj : null,
    vitals: toArray("vitals"),
    ventilation: toArray("ventilation"),
    diagnostics: toArray("diagnostics"),
    labValues: toArray("labValues"),
    exam: toArray("exam"),
    cpr: toArray("cpr"),
    tourniquets: toArray("tourniquet"),
    medications: toArray("medication"),
    surgeries: toArray("surgery"),
    attachments: Object.values(attachmentRows).filter(
      (a) => !a.deleted,
    ) as any[],
    hospitals,
  };
}

/** Coerce any value to a display string. */
const toStr = (v: unknown): string => (v == null ? "" : String(v));

/** Check if a row exists for a vitals/ventilation field. Returns true if row is present. */
function rowExistsForField(fieldId: string, data: any): boolean {
  // Parse field ID: p{page}-vitals-{index}-{suffix} or p{page}-vent-{index}-{suffix}
  const vitalsMatch = fieldId.match(/^p(\d)-vitals-(\d)/);
  if (vitalsMatch) {
    const page = Number(vitalsMatch[1]);
    const idx = Number(vitalsMatch[2]);
    const rows = (data.vitals || []).filter(
      (v: any) => (v.role ?? page) === page,
    );
    return !!rows[idx];
  }
  const ventMatch = fieldId.match(/^p(\d)-vent-(\d)/);
  if (ventMatch) {
    const page = Number(ventMatch[1]);
    const idx = Number(ventMatch[2]);
    const rows = (data.ventilation || []).filter(
      (v: any) => (v.role ?? page) === page,
    );
    return !!rows[idx];
  }
  return false;
}

/** Format a date string (yyyy-mm-dd or ISO) as dd.mm.yyyy for display */
function fmtDateValue(v: unknown): string {
  if (!v) return "";
  const s = String(v);
  // ISO or yyyy-mm-dd
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return `${m[3]}.${m[2]}.${m[1]}`;
  return s;
}

/** Centered popover for editing a single field value. */
function PopoverInput({
  value,
  fieldType,
  label,
  onCommit,
  onClose,
  colors,
}: {
  value: string;
  fieldType: string;
  label?: string;
  onCommit: (val: string) => void;
  onClose: () => void;
  colors: Record<string, string>;
}) {
  const [local, setLocal] = useState(value);
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null);
  const isMultiline = fieldType === "textarea";

  useEffect(() => {
    // Auto-focus and select on open
    requestAnimationFrame(() => {
      const el = inputRef.current;
      if (el) {
        el.focus();
        if ("select" in el) el.select();
      }
    });
  }, []);

  const commit = () => {
    onCommit(local);
    onClose();
  };

  const inputMode =
    fieldType === "number" ? ("numeric" as const) : ("text" as const);

  const inputStyle: React.CSSProperties = {
    width: "100%",
    boxSizing: "border-box",
    fontSize: "1em",
    fontFamily: "'e-Ukraine', system-ui, sans-serif",
    padding: "0.5em",
    border: `1px solid ${colors.border}`,
    borderRadius: "0.25em",
    outline: "none",
    background: colors.bg,
    color: colors.text,
  };

  return createPortal(
    <div
      style={POPOVER_BACKDROP}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) {
          commit();
        }
      }}
    >
      <div
        style={{
          background: colors.bg,
          border: `1px solid ${colors.border}`,
          borderRadius: "0.5em",
          padding: "0.75em",
          boxShadow: "0 0.5em 2em rgba(0,0,0,0.3)",
          width: isMultiline ? "min(90vw, 32em)" : "min(90vw, 25em)",
          fontSize: "1rem",
          lineHeight: "normal",
        }}
      >
        {label && (
          <div
            style={{
              fontSize: "0.8em",
              fontWeight: 600,
              color: colors.textMuted,
              marginBottom: "0.5em",
            }}
          >
            {label}
          </div>
        )}
        {isMultiline ? (
          <textarea
            ref={inputRef as React.RefObject<HTMLTextAreaElement>}
            value={local}
            rows={10}
            onChange={(e) => setLocal(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Escape") onClose();
            }}
            style={{ ...inputStyle, resize: "vertical", minHeight: "12em" }}
          />
        ) : (
          <input
            ref={inputRef as React.RefObject<HTMLInputElement>}
            type="text"
            inputMode={inputMode}
            enterKeyHint="done"
            value={local}
            onChange={(e) => setLocal(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") commit();
              if (e.key === "Escape") onClose();
            }}
            style={inputStyle}
          />
        )}
        <PopoverFooter
          onClear={() => { onCommit(""); onClose(); }}
          onCommit={commit}
          colors={colors}
          noBorder
        />
      </div>
    </div>,
    document.body,
  );
}

const BLOOD_TYPES = [
  { value: "A", label: "A" },
  { value: "B", label: "B" },
  { value: "AB", label: "AB" },
  { value: "O", label: "O" },
  { value: "unknown", label: "?" },
];
const RH_FACTORS = [
  { value: "positive", label: "+" },
  { value: "negative", label: "\u2212" },
  { value: "unknown", label: "?" },
];

/** Combined dual-column picker for blood type (ABO) + Rh factor. */
function BloodRhPickerPopover({
  bloodType,
  rhFactor,
  onCommit,
  onClose,
  colors,
}: {
  bloodType: string | null;
  rhFactor: string | null;
  onCommit: (bt: string | null, rh: string | null) => void;
  onClose: () => void;
  colors: Record<string, string>;
}) {
  const [bt, setBt] = useState(bloodType);
  const [rh, setRh] = useState(rhFactor);
  const btRef = useRef<HTMLDivElement>(null);
  const rhRef = useRef<HTMLDivElement>(null);

  const CELL = 2.5;

  useEffect(() => {
    requestAnimationFrame(() => {
      const btIdx = BLOOD_TYPES.findIndex((o) => o.value === bt);
      const rhIdx = RH_FACTORS.findIndex((o) => o.value === rh);
      if (btIdx >= 0)
        (
          btRef.current?.children[btIdx] as HTMLElement | undefined
        )?.scrollIntoView({ block: "center" });
      if (rhIdx >= 0)
        (
          rhRef.current?.children[rhIdx] as HTMLElement | undefined
        )?.scrollIntoView({ block: "center" });
    });
  }, []);

  const commit = () => {
    onCommit(bt, rh);
    onClose();
  };
  const clear = () => {
    onCommit(null, null);
    onClose();
  };

  const colStyle: React.CSSProperties = {
    flex: 1,
    height: `${CELL * 5}em`,
    overflowY: "auto",
    scrollSnapType: "y mandatory",
    WebkitOverflowScrolling: "touch",
  };

  const cellStyle = (selected: boolean): React.CSSProperties => ({
    height: `${CELL}em`,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "1.1em",
    cursor: "pointer",
    scrollSnapAlign: "start",
    fontWeight: selected ? 700 : 400,
    color: selected ? colors.formInk : colors.text,
    background: selected ? `${colors.formInk}18` : "transparent",
    borderRadius: "0.25em",
    transition: "background 0.1s",
  });

  const btLabel = BLOOD_TYPES.find((o) => o.value === bt)?.label ?? "";
  const rhLabel = RH_FACTORS.find((o) => o.value === rh)?.label ?? "";
  const preview =
    [btLabel, rhLabel ? `Rh${rhLabel}` : ""].filter(Boolean).join(" ") ||
    "\u2014";

  return createPortal(
    <div
      style={POPOVER_BACKDROP}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) commit();
      }}
    >
      <div
        style={{
          background: colors.bg,
          border: `1px solid ${colors.border}`,
          borderRadius: "0.5em",
          boxShadow: "0 0.5em 2em rgba(0,0,0,0.3)",
          width: "min(90vw, 16em)",
          fontSize: "1rem",
          lineHeight: "normal",
          overflow: "hidden",
        }}
      >
        {/* Header */}
        <div
          style={{
            fontSize: "0.8em",
            fontWeight: 600,
            color: colors.textMuted,
            padding: "0.5em 0.75em 0",
            textAlign: "center",
          }}
        >
          Група крові / Rh
        </div>
        <div
          style={{
            textAlign: "center",
            padding: "0.75em",
            borderBottom: `1px solid ${colors.border}`,
            fontFamily: "'e-Ukraine', system-ui, sans-serif",
            fontSize: "1.25em",
            fontWeight: 600,
            color: colors.formInk,
          }}
        >
          {preview}
        </div>

        {/* Two columns */}
        <div
          style={{
            padding: "0.4em 0.75em 0.15em",
            fontSize: "0.7em",
            fontWeight: 600,
            color: colors.textMuted,
            fontFamily: "'e-Ukraine', system-ui, sans-serif",
            textTransform: "uppercase",
            letterSpacing: "0.05em",
            display: "flex",
          }}
        >
          <span style={{ flex: 1 }}>Група крові</span>
          <span style={{ flex: 1, textAlign: "center" }}>Rh</span>
        </div>
        <div style={{ display: "flex", gap: 0 }}>
          {/* Blood type column */}
          <div ref={btRef} style={colStyle}>
            {BLOOD_TYPES.map((o) => (
              <div
                key={o.value}
                style={cellStyle(o.value === bt)}
                onClick={(e) => {
                  setBt(o.value);
                  (e.currentTarget as HTMLElement).scrollIntoView({
                    block: "center",
                    behavior: "smooth",
                  });
                }}
              >
                {o.label}
              </div>
            ))}
          </div>
          <div
            style={{ width: "1px", background: colors.border, flexShrink: 0 }}
          />
          {/* Rh column */}
          <div ref={rhRef} style={colStyle}>
            {RH_FACTORS.map((o) => (
              <div
                key={o.value}
                style={cellStyle(o.value === rh)}
                onClick={(e) => {
                  setRh(o.value);
                  (e.currentTarget as HTMLElement).scrollIntoView({
                    block: "center",
                    behavior: "smooth",
                  });
                }}
              >
                {o.label}
              </div>
            ))}
          </div>
        </div>

        <PopoverFooter onClear={clear} onCommit={commit} colors={colors} />
      </div>
    </div>,
    document.body,
  );
}

const VISION_VALUES = [
  { value: "1.0", label: "1.0" },
  { value: "0.9", label: "0.9" },
  { value: "0.8", label: "0.8" },
  { value: "0.7", label: "0.7" },
  { value: "0.6", label: "0.6" },
  { value: "0.5", label: "0.5" },
  { value: "0.4", label: "0.4" },
  { value: "0.3", label: "0.3" },
  { value: "0.2", label: "0.2" },
  { value: "0.1", label: "0.1" },
  { value: "CF", label: "CF" }, // Count Fingers
  { value: "HM", label: "HM" }, // Hand Motion
  { value: "LP", label: "LP" }, // Light Perception
  { value: "NLP", label: "NLP" }, // No Light Perception
];

/** Combined dual-column picker for vision (Right + Left eye). */
function VisionPickerPopover({
  visionR,
  visionL,
  onCommit,
  onClose,
  colors,
}: {
  visionR: string | null;
  visionL: string | null;
  onCommit: (r: string | null, l: string | null) => void;
  onClose: () => void;
  colors: Record<string, string>;
}) {
  const [right, setRight] = useState(visionR);
  const [left, setLeft] = useState(visionL);
  const rightRef = useRef<HTMLDivElement>(null);
  const leftRef = useRef<HTMLDivElement>(null);

  const CELL = 2.5;

  useEffect(() => {
    requestAnimationFrame(() => {
      const rIdx = VISION_VALUES.findIndex((o) => o.value === right);
      const lIdx = VISION_VALUES.findIndex((o) => o.value === left);
      if (rIdx >= 0)
        (
          rightRef.current?.children[rIdx] as HTMLElement | undefined
        )?.scrollIntoView({ block: "center" });
      if (lIdx >= 0)
        (
          leftRef.current?.children[lIdx] as HTMLElement | undefined
        )?.scrollIntoView({ block: "center" });
    });
  }, []);

  const commit = () => {
    onCommit(right, left);
    onClose();
  };
  const clear = () => {
    onCommit(null, null);
    onClose();
  };

  const colStyle: React.CSSProperties = {
    flex: 1,
    height: `${CELL * 5}em`,
    overflowY: "auto",
    scrollSnapType: "y mandatory",
    WebkitOverflowScrolling: "touch",
  };

  const cellStyle = (selected: boolean): React.CSSProperties => ({
    height: `${CELL}em`,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "1.1em",
    cursor: "pointer",
    scrollSnapAlign: "start",
    fontWeight: selected ? 700 : 400,
    color: selected ? colors.formInk : colors.text,
    background: selected ? `${colors.formInk}18` : "transparent",
    borderRadius: "0.25em",
    transition: "background 0.1s",
  });

  const rLabel =
    VISION_VALUES.find((o) => o.value === right)?.label ?? right ?? "";
  const lLabel =
    VISION_VALUES.find((o) => o.value === left)?.label ?? left ?? "";
  const preview =
    [rLabel ? `П: ${rLabel}` : "", lLabel ? `Л: ${lLabel}` : ""]
      .filter(Boolean)
      .join("  ") || "\u2014";

  return createPortal(
    <div
      style={POPOVER_BACKDROP}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) commit();
      }}
    >
      <div
        style={{
          background: colors.bg,
          border: `1px solid ${colors.border}`,
          borderRadius: "0.5em",
          boxShadow: "0 0.5em 2em rgba(0,0,0,0.3)",
          width: "min(90vw, 16em)",
          fontSize: "1rem",
          lineHeight: "normal",
          overflow: "hidden",
        }}
      >
        {/* Header */}
        <div
          style={{
            fontSize: "0.8em",
            fontWeight: 600,
            color: colors.textMuted,
            padding: "0.5em 0.75em 0",
            textAlign: "center",
          }}
        >
          Зір
        </div>
        <div
          style={{
            textAlign: "center",
            padding: "0.75em",
            borderBottom: `1px solid ${colors.border}`,
            fontFamily: "'e-Ukraine', system-ui, sans-serif",
            fontSize: "1.25em",
            fontWeight: 600,
            color: colors.formInk,
          }}
        >
          {preview}
        </div>

        {/* Two columns */}
        <div
          style={{
            padding: "0.4em 0.75em 0.15em",
            fontSize: "0.7em",
            fontWeight: 600,
            color: colors.textMuted,
            fontFamily: "'e-Ukraine', system-ui, sans-serif",
            textTransform: "uppercase",
            letterSpacing: "0.05em",
            display: "flex",
          }}
        >
          <span style={{ flex: 1 }}>Правий (OD)</span>
          <span style={{ flex: 1, textAlign: "center" }}>Лівий (OS)</span>
        </div>
        <div style={{ display: "flex", gap: 0 }}>
          {/* Right eye column */}
          <div ref={rightRef} style={colStyle}>
            {VISION_VALUES.map((o) => (
              <div
                key={o.value}
                style={cellStyle(o.value === right)}
                onClick={(e) => {
                  setRight(o.value);
                  (e.currentTarget as HTMLElement).scrollIntoView({
                    block: "center",
                    behavior: "smooth",
                  });
                }}
              >
                {o.label}
              </div>
            ))}
          </div>
          <div
            style={{ width: "1px", background: colors.border, flexShrink: 0 }}
          />
          {/* Left eye column */}
          <div ref={leftRef} style={colStyle}>
            {VISION_VALUES.map((o) => (
              <div
                key={o.value}
                style={cellStyle(o.value === left)}
                onClick={(e) => {
                  setLeft(o.value);
                  (e.currentTarget as HTMLElement).scrollIntoView({
                    block: "center",
                    behavior: "smooth",
                  });
                }}
              >
                {o.label}
              </div>
            ))}
          </div>
        </div>

        <PopoverFooter onClear={clear} onCommit={commit} colors={colors} />
      </div>
    </div>,
    document.body,
  );
}

/** Popover for selecting from a list of options (replaces native <select>). */
function SelectPopover({
  value,
  options,
  label,
  onCommit,
  onClose,
  colors,
}: {
  value: string;
  options: { value: string; label: string }[];
  label?: string;
  onCommit: (val: string) => void;
  onClose: () => void;
  colors: Record<string, string>;
}) {
  const select = (v: string) => {
    onCommit(v);
    onClose();
  };
  const multiCol = options.length > 8;

  const optStyle = (selected: boolean): React.CSSProperties => ({
    padding: "0.75em 1em",
    cursor: "pointer",
    borderBottom: `1px solid ${colors.border}`,
    fontWeight: selected ? 700 : 400,
    color: selected ? colors.formInk : colors.text,
    background: selected ? `${colors.formInk}18` : "transparent",
    fontSize: "1.1em",
    fontFamily: "'e-Ukraine', system-ui, sans-serif",
    display: "flex",
    alignItems: "center",
    gap: "0.5em",
    ...(multiCol ? { breakInside: "avoid" as const } : {}),
  });

  return createPortal(
    <div
      style={POPOVER_BACKDROP}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        style={{
          background: colors.bg,
          border: `1px solid ${colors.border}`,
          borderRadius: "0.5em",
          boxShadow: "0 0.5em 2em rgba(0,0,0,0.3)",
          width: multiCol ? "min(90vw, 30em)" : "min(90vw, 14em)",
          fontSize: "1rem",
          lineHeight: "normal",
          overflow: "hidden",
        }}
      >
        {label && (
          <div
            style={{
              padding: "0.6em 1em",
              fontWeight: 600,
              fontSize: "0.85em",
              color: colors.textMuted,
              borderBottom: `1px solid ${colors.border}`,
            }}
          >
            {label}
          </div>
        )}
        <div
          style={
            multiCol
              ? {
                  columnCount: 2,
                  columnGap: 0,
                  columnRule: `1px solid ${colors.border}`,
                  maxHeight: "80vh",
                  overflowY: "auto",
                }
              : undefined
          }
        >
          {options.map((o) => (
            <div
              key={o.value}
              style={optStyle(o.value === value)}
              onClick={() => select(o.value)}
              onMouseEnter={(e) => {
                if (o.value !== value)
                  e.currentTarget.style.background = colors.bgAlt;
              }}
              onMouseLeave={(e) => {
                if (o.value !== value)
                  e.currentTarget.style.background = "transparent";
              }}
            >
              <span
                style={{
                  width: "1.2em",
                  textAlign: "center",
                  flexShrink: 0,
                  opacity: o.value === value ? 1 : 0,
                }}
              >
                {IconTick}
              </span>
              {o.label}
            </div>
          ))}
          {/* Clear option */}
          <div
            style={{
              ...optStyle(!value),
              color: colors.textMuted,
              fontStyle: "italic",
            }}
            onClick={() => select("")}
            onMouseEnter={(e) => {
              if (value) e.currentTarget.style.background = colors.bgAlt;
            }}
            onMouseLeave={(e) => {
              if (value) e.currentTarget.style.background = "transparent";
            }}
          >
            <span
              style={{
                width: "1.2em",
                textAlign: "center",
                flexShrink: 0,
                opacity: !value ? 1 : 0,
              }}
            >
              {IconTick}
            </span>
            —
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}

/** Popover for editing patient name with autocomplete suggestions. */
function PatientNamePopover({
  value,
  label,
  onCommitName,
  onPickPatient,
  onCreateNew,
  onCancel,
  searchFn,
  colors,
}: {
  value: string;
  label?: string;
  onCommitName: (name: string) => void;
  onPickPatient: (patientId: string) => void;
  onCreateNew: (name: string) => void;
  onCancel: () => void;
  searchFn: (term: string) => Promise<PatientListItem[]>;
  colors: Record<string, string>;
}) {
  const { isAnon } = useAnon();
  const [local, setLocal] = useState(value);
  const [suggestions, setSuggestions] = useState<PatientListItem[]>([]);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const doneRef = useRef(false);

  useEffect(() => {
    requestAnimationFrame(() => inputRef.current?.select());
    // Pre-populate suggestions immediately on open
    searchFn(value)
      .then(setSuggestions)
      .catch(() => {});
  }, []);

  const doSearch = (term: string) => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(async () => {
      try {
        const results = await searchFn(term);
        setSuggestions(results);
      } catch {
        setSuggestions([]);
      }
    }, 250);
  };

  const commit = () => {
    if (doneRef.current) return;
    doneRef.current = true;
    onCommitName(local);
  };

  const cancel = () => {
    if (doneRef.current) return;
    doneRef.current = true;
    onCancel();
  };

  const pick = (id: string) => {
    doneRef.current = true;
    onPickPatient(id);
  };

  return createPortal(
    <div
      style={POPOVER_BACKDROP}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) commit();
      }}
    >
      <div
        style={{
          background: colors.bg,
          border: `1px solid ${colors.border}`,
          borderRadius: "0.5em",
          padding: "0.75em",
          boxShadow: "0 0.5em 2em rgba(0,0,0,0.3)",
          width: "min(90vw, 30em)",
          fontSize: "1rem",
          lineHeight: "normal",
        }}
      >
        {label && (
          <div
            style={{
              fontSize: "0.8em",
              fontWeight: 600,
              color: colors.textMuted,
              marginBottom: "0.5em",
            }}
          >
            {label}
          </div>
        )}
        <input
          ref={inputRef}
          type="text"
          enterKeyHint="done"
          value={local}
          placeholder="ПІБ пацієнта"
          onChange={(e) => {
            setLocal(e.target.value);
            doSearch(e.target.value);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") commit();
            if (e.key === "Escape") cancel();
          }}
          style={{
            width: "100%",
            boxSizing: "border-box",
            fontSize: "1.125em",
            fontFamily: "'e-Ukraine', system-ui, sans-serif",
            padding: "0.6em 0.75em",
            border: `1px solid ${colors.border}`,
            borderRadius: "0.375em",
            outline: "none",
            background: colors.bg,
            color: colors.text,
          }}
        />
        {suggestions.length > 0 && (
          <div
            style={{
              marginTop: "0.375em",
              maxHeight: "50vh",
              overflowY: "auto",
              border: `1px solid ${colors.border}`,
              borderRadius: "0.375em",
            }}
          >
            {suggestions.map((p) => (
              <div
                key={p.id}
                onClick={() => pick(p.id)}
                style={{
                  padding: "0.6em 0.75em",
                  cursor: "pointer",
                  borderBottom: `1px solid ${colors.border}`,
                }}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.background = colors.bgAlt)
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.background = "transparent")
                }
              >
                <div
                  style={{
                    fontWeight: 500,
                    fontSize: "0.95em",
                    color:
                      p.name && p.name.trim() ? colors.text : colors.textMuted,
                    fontStyle: p.name && p.name.trim() ? "normal" : "italic",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {isAnon ? "—" : ((p.name && p.name.trim()) || `(${p.id.slice(0, 8)})`)}
                </div>
                {(!isAnon && (p.dateOfBirth || p.sex)) && (
                  <div
                    style={{
                      fontSize: "0.8em",
                      color: colors.textMuted,
                      marginTop: "0.125em",
                    }}
                  >
                    {[
                      p.dateOfBirth,
                      p.sex === "male"
                        ? "\u2642"
                        : p.sex === "female"
                          ? "\u2640"
                          : null,
                    ]
                      .filter(Boolean)
                      .join("  ")}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
        <PopoverFooter
          onClear={() => { if (doneRef.current) return; doneRef.current = true; onCommitName(""); }}
          onCommit={commit}
          colors={colors}
          noBorder
        >
          <button
            onClick={() => { doneRef.current = true; onCreateNew(local); }}
            style={{ ...popoverBtnStyle(colors), color: colors.text, fontSize: "0.8em" }}
          >
            + Новий пацієнт
          </button>
        </PopoverFooter>
      </div>
    </div>,
    document.body,
  );
}

function UnitPopover({
  value,
  label,
  usageMap,
  onSelect,
  onClear,
  onClose,
  colors,
}: {
  value: string;
  label?: string;
  usageMap?: Map<string, number>;
  onSelect: (unitId: string) => void;
  onClear: () => void;
  onClose: () => void;
  colors: Record<string, string>;
}) {
  const rpc = useRpc();
  const dispatch = useCommand();
  const [suggestions, setSuggestions] = useState<{ id: string; name: string | null }[]>([]);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingSearchRef = useRef<Promise<{ id: string; name: string | null }[]> | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const doneRef = useRef(false);

  useEffect(() => {
    requestAnimationFrame(() => inputRef.current?.focus());
    doSearch("");
  }, []);

  const doSearch = (term: string) => {
    if (timerRef.current) clearTimeout(timerRef.current);
    pendingSearchRef.current = new Promise((resolve) => {
      timerRef.current = setTimeout(async () => {
        try {
          const result = await rpc<{ units: { id: string; name: string | null }[] }>({
            type: "searchUnits",
            search: term || undefined,
          });
          const units = result.units;
          if (usageMap && usageMap.size > 0) {
            units.sort((a, b) => (usageMap.get(b.id) ?? 0) - (usageMap.get(a.id) ?? 0));
          }
          setSuggestions(units);
          // Inline type-ahead: fill the input with first suggestion, selected
          const input = inputRef.current;
          if (input && term && units.length > 0) {
            const first = units[0].name ?? "";
            if (first.toLowerCase().startsWith(term.toLowerCase())) {
              input.value = term + first.slice(term.length);
              requestAnimationFrame(() => input.setSelectionRange(term.length, input.value.length));
            }
          }
          resolve(units);
        } catch {
          setSuggestions([]);
          resolve([]);
        }
      }, 250);
    });
  };

  // Returns the text the user actually typed (before any autocomplete selection)
  const getTyped = () => {
    const input = inputRef.current;
    if (!input) return "";
    return input.value.slice(0, input.selectionStart ?? input.value.length);
  };

  const commit = async () => {
    if (doneRef.current) return;
    // Await any pending search so we have fresh suggestions
    const latest = pendingSearchRef.current ? await pendingSearchRef.current : suggestions;
    doneRef.current = true;
    const name = getTyped().trim();
    if (!name && latest.length === 0) { onClose(); return; }
    // Prefer exact match, then first suggestion, then auto-create
    const exact = latest.find(
      (u) => u.name?.toLowerCase() === name.toLowerCase(),
    );
    if (exact) { onSelect(exact.id); return; }
    if (latest.length > 0) { onSelect(latest[0].id); return; }
    // No suggestions — auto-create the unit
    const uuid = uuidv7();
    await dispatch("unit.create", { uuid });
    await dispatch("unit.setName", { uuid, name });
    onSelect(uuid);
  };

  const pick = (unitId: string) => {
    if (doneRef.current) return;
    doneRef.current = true;
    onSelect(unitId);
  };

  return createPortal(
    <div
      style={POPOVER_BACKDROP}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) commit();
      }}
    >
      <div
        style={{
          background: colors.bg,
          border: `1px solid ${colors.border}`,
          borderRadius: "0.5em",
          padding: "0.75em",
          boxShadow: "0 0.5em 2em rgba(0,0,0,0.3)",
          width: "min(90vw, 30em)",
          fontSize: "1rem",
          lineHeight: "normal",
        }}
      >
        {label && (
          <div
            style={{
              fontSize: "0.8em",
              fontWeight: 600,
              color: colors.textMuted,
              marginBottom: "0.5em",
            }}
          >
            {label}
          </div>
        )}
        <input
          ref={inputRef}
          type="text"
          enterKeyHint="done"
          defaultValue=""
          placeholder="Військова частина"
          onChange={(e) => {
            // Use only the typed portion (before any autocomplete selection)
            const el = e.target;
            const typed = el.value.slice(0, el.selectionStart ?? el.value.length);
            doSearch(typed);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") { e.preventDefault(); commit(); }
            if (e.key === "Escape") onClose();
          }}
          style={{
            width: "100%",
            boxSizing: "border-box",
            fontSize: "1.125em",
            fontFamily: "'e-Ukraine', system-ui, sans-serif",
            padding: "0.6em 0.75em",
            border: `1px solid ${colors.border}`,
            borderRadius: "0.375em",
            outline: "none",
            background: colors.bg,
            color: colors.text,
          }}
        />
        {suggestions.length > 0 && (
          <div
            style={{
              marginTop: "0.375em",
              maxHeight: "50vh",
              overflowY: "auto",
              border: `1px solid ${colors.border}`,
              borderRadius: "0.375em",
            }}
          >
            {suggestions.map((u) => (
              <div
                key={u.id}
                onClick={() => pick(u.id)}
                style={{
                  padding: "0.6em 0.75em",
                  cursor: "pointer",
                  borderBottom: `1px solid ${colors.border}`,
                }}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.background = colors.bgAlt)
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.background = "transparent")
                }
              >
                <span
                  style={{
                    fontWeight: 500,
                    fontSize: "0.95em",
                    color: u.name ? colors.text : colors.textMuted,
                    fontStyle: u.name ? "normal" : "italic",
                  }}
                >
                  {u.name || `(${u.id.slice(0, 8)})`}
                </span>
              </div>
            ))}
          </div>
        )}
        <PopoverFooter
          onClear={() => { if (doneRef.current) return; doneRef.current = true; onClear(); }}
          onCommit={commit}
          colors={colors}
          noBorder
        />
      </div>
    </div>,
    document.body,
  );
}

/**
 * Facility picker: dropdown of own units for medics, search-as-you-type for others.
 * Always stores the unit ID via the field's command.
 */
function FacilityPopover({
  value,
  label,
  userUnits,
  usageMap,
  onSelect,
  onClear,
  onClose,
  colors,
}: {
  value: string;
  label?: string;
  userUnits: { id: string; name: string | null }[];
  usageMap?: Map<string, number>;
  onSelect: (unitId: string) => void;
  onClear: () => void;
  onClose: () => void;
  colors: Record<string, string>;
}) {
  // Medics with assigned units get a simple dropdown
  if (userUnits.length > 0) {
    return createPortal(
      <div
        style={POPOVER_BACKDROP}
        onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
      >
        <div
          style={{
            background: colors.bg,
            border: `1px solid ${colors.border}`,
            borderRadius: "0.5em",
            padding: "0.75em",
            boxShadow: "0 0.5em 2em rgba(0,0,0,0.3)",
            width: "min(90vw, 30em)",
            fontSize: "1rem",
            lineHeight: "normal",
          }}
        >
          {label && (
            <div style={{ fontSize: "0.8em", fontWeight: 600, color: colors.textMuted, marginBottom: "0.5em" }}>
              {label}
            </div>
          )}
          <div style={{ maxHeight: "50vh", overflowY: "auto", border: `1px solid ${colors.border}`, borderRadius: "0.375em" }}>
            {userUnits.map((u) => (
              <div
                key={u.id}
                onClick={() => onSelect(u.id)}
                style={{
                  padding: "0.6em 0.75em",
                  cursor: "pointer",
                  borderBottom: `1px solid ${colors.border}`,
                  background: u.id === value ? colors.bgAlt : "transparent",
                  fontWeight: u.id === value ? 600 : 500,
                  fontSize: "0.95em",
                  color: colors.text,
                }}
                onMouseEnter={(e) => (e.currentTarget.style.background = colors.bgAlt)}
                onMouseLeave={(e) => (e.currentTarget.style.background = u.id === value ? colors.bgAlt : "transparent")}
              >
                {u.name || `(${u.id.slice(0, 8)})`}
              </div>
            ))}
          </div>
          <PopoverFooter onClear={onClear} onCommit={onClose} colors={colors} noBorder />
        </div>
      </div>,
      document.body,
    );
  }

  // Non-medics: full search-as-you-type
  return (
    <UnitPopover
      value=""
      label={label}
      usageMap={usageMap}
      onSelect={onSelect}
      onClear={onClear}
      onClose={onClose}
      colors={colors}
    />
  );
}

/** Display a signature thumbnail from blob storage */
function SignatureThumbnail({
  sha256,
  invert,
}: {
  sha256: string;
  invert?: boolean;
}) {
  const [url, setUrl] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    let objectUrl: string | null = null;

    getBlob(sha256).then((result) => {
      if (cancelled || !result) return;
      const buffer = result.data.buffer.slice(
        result.data.byteOffset,
        result.data.byteOffset + result.data.byteLength,
      ) as ArrayBuffer;
      objectUrl = URL.createObjectURL(
        new Blob([buffer], { type: "image/png" }),
      );
      setUrl(objectUrl);
    });

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [sha256]);

  if (!url) return null;
  return (
    <img
      src={url}
      alt=""
      style={{
        maxWidth: "100%",
        maxHeight: "100%",
        objectFit: "contain",
        filter: invert ? "invert(1)" : undefined,
      }}
    />
  );
}

/** Signature drawing popover with canvas */
function SignaturePopover({
  sha256,
  label,
  onCommit,
  onClose,
  colors,
}: {
  sha256: string | null;
  label: string;
  onCommit: (newSha256: string | null) => void;
  onClose: () => void;
  colors: ReturnType<typeof useColors>;
}) {
  const { t } = useI18n();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasDrawn, setHasDrawn] = useState(false);
  const lastPosRef = useRef<{ x: number; y: number } | null>(null);

  // Canvas dimensions (2x for retina) - wide to match signature underline
  const CANVAS_W = 600;
  const CANVAS_H = 80;
  const SCALE = 2;

  // Initialize canvas
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    // Set canvas size for retina
    canvas.width = CANVAS_W * SCALE;
    canvas.height = CANVAS_H * SCALE;
    ctx.scale(SCALE, SCALE);

    // Transparent background
    ctx.clearRect(0, 0, CANVAS_W, CANVAS_H);

    // Drawing style
    ctx.strokeStyle = "#000000";
    ctx.lineWidth = 3;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
  }, []);

  const getPos = useCallback((e: React.PointerEvent) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    const rect = canvas.getBoundingClientRect();
    return {
      x: (e.clientX - rect.left) * (CANVAS_W / rect.width),
      y: (e.clientY - rect.top) * (CANVAS_H / rect.height),
    };
  }, []);

  const handlePointerDown = useCallback(
    (e: React.PointerEvent) => {
      e.preventDefault();
      const canvas = canvasRef.current;
      if (!canvas) return;
      canvas.setPointerCapture(e.pointerId);
      setIsDrawing(true);
      lastPosRef.current = getPos(e);
    },
    [getPos],
  );

  const handlePointerMove = useCallback(
    (e: React.PointerEvent) => {
      if (!isDrawing) return;
      const canvas = canvasRef.current;
      const ctx = canvas?.getContext("2d");
      if (!canvas || !ctx || !lastPosRef.current) return;

      const pos = getPos(e);
      ctx.beginPath();
      ctx.moveTo(lastPosRef.current.x, lastPosRef.current.y);
      ctx.lineTo(pos.x, pos.y);
      ctx.stroke();
      lastPosRef.current = pos;
      setHasDrawn(true);
    },
    [isDrawing, getPos],
  );

  const handlePointerUp = useCallback((e: React.PointerEvent) => {
    const canvas = canvasRef.current;
    if (canvas) canvas.releasePointerCapture(e.pointerId);
    setIsDrawing(false);
    lastPosRef.current = null;
  }, []);

  const handleErase = useCallback(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext("2d");
    if (!canvas || !ctx) return;
    ctx.clearRect(0, 0, CANVAS_W, CANVAS_H);
    setHasDrawn(false);
  }, []);

  const handleConfirm = useCallback(async () => {
    const canvas = canvasRef.current;
    if (!canvas) {
      onClose();
      return;
    }

    if (!hasDrawn) {
      // No drawing - keep existing or set null
      onCommit(sha256);
      return;
    }

    // Export canvas as PNG
    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, "image/png"),
    );
    if (!blob) {
      onClose();
      return;
    }

    // Store in blob storage
    const data = new Uint8Array(await blob.arrayBuffer());
    const newSha256 = await storeBlob(data, "image/png");
    onCommit(newSha256);
  }, [hasDrawn, sha256, onCommit, onClose]);

  return createPortal(
    <div
      style={POPOVER_BACKDROP}
      onClick={(e) => {
        if (e.target === e.currentTarget) handleConfirm();
      }}
    >
      <div
        style={{
          background: colors.bg,
          borderRadius: "0.5em",
          boxShadow: "0 4px 24px rgba(0,0,0,0.2)",
          padding: "1em",
          minWidth: "320px",
          maxWidth: "95vw",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          style={{
            fontWeight: 600,
            marginBottom: "0.75em",
            color: colors.text,
          }}
        >
          {label}
        </div>
        <canvas
          ref={canvasRef}
          style={{
            width: "100%",
            height: "auto",
            aspectRatio: `${CANVAS_W} / ${CANVAS_H}`,
            border: `1px solid ${colors.border}`,
            borderRadius: "0.25em",
            touchAction: "none",
            cursor: "crosshair",
            display: "block",
            background: "#ffffff",
          }}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerCancel={handlePointerUp}
        />
        <div
          style={{
            fontSize: "0.75em",
            color: colors.textMuted,
            marginTop: "0.5em",
          }}
        >
          {t("form.signatureHint")}
        </div>
        <PopoverFooter
          onClear={() => onCommit(null)}
          onCommit={handleConfirm}
          colors={colors}
          noBorder
        >
          <button onClick={handleErase} style={popoverBtnStyle(colors)}>
            {t("form.signatureErase")}
          </button>
        </PopoverFooter>
      </div>
    </div>,
    document.body,
  );
}

/** Resolve a human-readable label for a field's edit popover */
function getFieldLabel(id: string, t: (key: string) => string): string {
  const labels: Record<string, string> = {
    "p1-case-number": t("form.caseNumber"),
    "p1-name": t("form.name"),
    "p1-id": t("form.idNumber"),
    "p1-dob": t("form.dob"),
    "p1-rank": t("form.rank"),
    "p1-unit": t("form.unit"),
    "p1-allergy-details": t("form.allergyDetails"),
    "p1-event-time": t("form.eventTime"),
    "p1-event-date": t("form.eventDate"),
    "p1-arrival-time": t("form.arrivalTime"),
    "p1-arrival-date": t("form.arrivalDate"),
    "p1-facility": t("form.facility"),
    "p1-cpr-start": t("form.cprStart"),
    "p1-cpr-end": t("form.cprEnd"),
    "p1-death-time": t("form.deathTime"),
    "p1-death-date": t("form.deathDate"),
    "p1-death-circumstances": t("form.deathCircumstances"),
    "p1-blood-type": t("form.bloodType"),
    "p1-rh-factor": t("form.rhFactor"),
    "p1-tetanus": t("form.tetanus"),
    "p1-kit1": t("form.kit1"),
    "p1-kit2": t("form.kit2"),
    "p1-notes": t("form.notes"),
    "p1-diagnosis": t("form.diagnosis"),
    "p1-discharge-time": t("form.dischargeTime"),
    "p1-discharge-date": t("form.dischargeDate"),
    "p1-discharge-destination": t("form.dischargeDestination"),
    "p1-medic-name": t("form.medicName"),
    "p1-medic-facility": t("form.medicFacility"),
    "p1-diag-xray": t("form.diagXray"),
    "p1-diag-otoscopy-r": t("form.diagOtoscopyR"),
    "p1-diag-otoscopy-l": t("form.diagOtoscopyL"),
    "p1-diag-pupils-r": t("form.diagPupilsR"),
    "p1-diag-pupils-l": t("form.diagPupilsL"),
    "p1-diag-ecg": t("form.diagEcg"),
    "p2-arrival-time": t("form.arrivalTimeR2"),
    "p2-arrival-date": t("form.arrivalDateR2"),
    "p2-cpr-start": t("form.cprStart"),
    "p2-cpr-end": t("form.cprEnd"),
    "p2-death-time": t("form.deathTime"),
    "p2-death-date": t("form.deathDate"),
    "p2-death-circumstances": t("form.deathCircumstances"),
    "p2-blood-type": t("form.bloodType"),
    "p2-rh-factor": t("form.rhFactor"),
    "p2-tetanus": t("form.tetanus"),
    "p2-kit1": t("form.kit1"),
    "p2-kit2": t("form.kit2"),
    "p2-notes": t("form.notes"),
    "p2-diagnosis": t("form.diagnosis"),
    "p2-discharge-time": t("form.dischargeTime"),
    "p2-discharge-date": t("form.dischargeDate"),
    "p2-discharge-destination": t("form.dischargeDestination"),
    "p2-medic-name": t("form.medicName"),
    "p2-medic-facility": t("form.medicFacility"),
    "p1-signature": t("form.signature"),
    "p2-signature": t("form.signature"),
    "p2-exam-airway-type": t("form.examAirwayType"),
    "p2-exam-vision-r": t("form.examVisionR"),
    "p2-exam-vision-l": t("form.examVisionL"),
    "p2-exam-gcs": t("form.examGcs"),
    "p2-surgery-desc": t("form.surgeryDesc"),
    "p2-surgery-start": t("form.surgeryStart"),
    "p2-surgery-start-date": t("form.surgeryStartDate"),
    "p2-surgery-end": t("form.surgeryEnd"),
    "p2-surgery-end-date": t("form.surgeryEndDate"),
    "p2-diag-xray": t("form.diagXray"),
    "p2-diag-ct": t("form.diagCt"),
    "p2-diag-ecg": t("form.diagEcg"),
    "p2-diag-us": t("form.diagUs"),
    "p1-r1care-tourniquetDetail": t("form.tourniquetLocation"),
  };
  if (labels[id]) return labels[id];

  if (id.startsWith("p1-prior-")) {
    const key = id.slice(9); // strip 'p1-prior-'
    const simple: Record<string, string> = {
      "care-time": t("form.priorCareTime"),
      "care-date": t("form.priorCareDate"),
      providerSelf: t("form.priorProviderSelf"),
      providerNonPro: t("form.priorProviderNonPro"),
      providerMedPro: t("form.priorProviderMedPro"),
      providerUnknown: t("form.priorProviderUnknown"),
      providerNone: t("form.priorProviderNone"),
      assessWarm: t("form.priorAssessWarm"),
      assessCold: t("form.priorAssessCold"),
      assessRR: t("form.priorAssessRR"),
      assessSpO2: t("form.priorAssessSpO2"),
      assessPulse: t("form.priorAssessPulse"),
      assessSystolic: t("form.priorAssessSystolic"),
      assessDiastolic: t("form.priorAssessDiastolic"),
      assessAVPU: t("form.priorAssessAVPU"),
      assessPain: t("form.priorAssessPain"),
      bandage: t("form.priorBandage"),
      packing: t("form.priorPacking"),
      oxygen: t("form.priorOxygen"),
      ofp: t("form.priorOfp"),
      nfp: t("form.priorNfp"),
      ngp: t("form.priorNgp"),
      ett: t("form.priorEtt"),
      cricothyrotomy: t("form.priorCricothyrotomy"),
      needleDecompression: t("form.priorNeedleDecompression"),
      occlusiveDressing: t("form.priorOcclusiveDressing"),
      bvm: t("form.priorBvm"),
      ivAccess: t("form.priorIvAccess"),
      ioAccess: t("form.priorIoAccess"),
      hypothermiaPrevention: t("form.priorHypothermiaPrevention"),
      immobilization: t("form.priorImmobilization"),
      eyeShield: t("form.priorEyeShield"),
      other: t("form.priorOther"),
      tourniquetRUE: t("form.priorTourniquetRUE"),
      tourniquetLUE: t("form.priorTourniquetLUE"),
      tourniquetRLE: t("form.priorTourniquetRLE"),
      tourniquetLLE: t("form.priorTourniquetLLE"),
      tourniquetAT: t("form.priorTourniquetAT"),
      tourniquetVT: t("form.priorTourniquetVT"),
      tourniquetDetail: t("form.tourniquetLocation"),
      medPillPack: t("form.priorMedPillPack"),
      medTXA: t("form.priorMedTXA"),
      medAnalgesic: t("form.priorMedAnalgesic"),
      medAntibiotic: t("form.priorMedAntibiotic"),
    };
    if (simple[key]) return simple[key];
    // Tourniquet time grid: tourniquetRUEApp → "RUE" + " " + "Application"
    const tqMatch = key.match(
      /^tourniquet(RUE|LUE|RLE|LLE|AT)(App|Rem|Conv|Repo)$/,
    );
    if (tqMatch) {
      const limbs: Record<string, string> = {
        RUE: t("form.priorTqRUE"),
        LUE: t("form.priorTqLUE"),
        RLE: t("form.priorTqRLE"),
        LLE: t("form.priorTqLLE"),
        AT: t("form.priorTqAT"),
      };
      const actions: Record<string, string> = {
        App: t("form.tqApp"),
        Rem: t("form.tqRem"),
        Conv: t("form.tqConv"),
        Repo: t("form.tqRepo"),
      };
      return limbs[tqMatch[1]] + " " + actions[tqMatch[2]];
    }
    // Medication detail grid: medTXADose → "Dose" + " " + "TXA"
    const medMatch = key.match(
      /^med(PillPack|TXA|Analgesic|Antibiotic|Extra)(Detail|Dose|Route|Time)$/,
    );
    if (medMatch) {
      const meds: Record<string, string> = {
        PillPack: t("form.priorMedPillPack"),
        TXA: t("form.priorMedTXA"),
        Analgesic: t("form.priorMedAnalgesic"),
        Antibiotic: t("form.priorMedAntibiotic"),
        Extra: t("form.priorMedExtra"),
      };
      const fields: Record<string, string> = {
        Detail: t("form.priorMedDetail"),
        Dose: t("form.medDose"),
        Route: t("form.medRoute"),
        Time: t("form.medTime"),
      };
      return fields[medMatch[2]] + " " + meds[medMatch[1]];
    }
    return id;
  }

  const suffix = id.split("-").pop() || "";

  if (id.includes("-vitals-")) {
    const m: Record<string, string> = {
      time: t("form.vitalsTime"),
      rr: t("form.vitalsRr"),
      spo2: t("form.vitalsSpo2"),
      pulse: t("form.vitalsPulse"),
      sys: t("form.vitalsSys"),
      dia: t("form.vitalsDia"),
      gcs: t("form.vitalsGcs"),
      pain: t("form.vitalsPain"),
      temp: t("form.vitalsTemp"),
      diuresis: t("form.vitalsDiuresis"),
    };
    return m[suffix] || id;
  }
  if (id.includes("-vent-")) {
    const m: Record<string, string> = {
      time: t("form.ventTime"),
      mode: t("form.ventMode"),
      rate: t("form.ventRate"),
      tv: t("form.ventTv"),
      fio2: t("form.ventFio2"),
      peep: t("form.ventPeep"),
      pip: t("form.ventPip"),
      etco2: t("form.ventEtco2"),
    };
    return m[suffix] || id;
  }
  if (id.includes("-med-")) {
    const m: Record<string, string> = {
      name: t("form.medName"),
      dose: t("form.medDose"),
      route: t("form.medRoute"),
      time: t("form.medTime"),
    };
    return m[suffix] || id;
  }
  if (id.includes("-tq-")) {
    const m: Record<string, string> = {
      app: t("form.tqApp"),
      rem: t("form.tqRem"),
      conv: t("form.tqConv"),
      repo: t("form.tqRepo"),
    };
    return m[suffix] || id;
  }
  if (id.includes("-lab-")) {
    const labId = id.replace(/^p2-lab-/, "");
    // Lab values use standard scientific abbreviations — no translation needed
    const m: Record<string, string> = {
      hg: "Hg",
      hct: "Hct",
      rbc: "RBC",
      wbc: "WBC",
      plt: "PLT",
      ph: "pH",
      pco2: "pCO₂",
      po2: "pO₂",
      hco3: "HCO₃",
      na: "Na",
      k: "K",
      ca: "Ca",
      cl: "Cl",
      lactate: "Lactate",
      urea: "Urea",
      creatinine: "Creatinine",
      glucose: "Glucose",
    };
    return m[labId] || id;
  }

  return id;
}

// Baseline offset: field.y is the underline position; for single-line
// text with lineHeight = box height, the CSS baseline sits at roughly
// this fraction of the height from the top of the box.  Calibrated so
// text lands just above the printed underline (e-Ukraine / system-ui).
const BASELINE_FRAC = 0.82;

// ─── Editor override support ─────────────────────────────────────────
// In dev/debug mode, apply position overrides from the field editor

function applyEditorOverrides(fields: FieldOverlay[]): FieldOverlay[] {
  try {
    const raw = localStorage.getItem("fieldEditor.overrides");
    if (!raw) return fields;
    const overrides: Record<
      string,
      {
        x?: number;
        y?: number;
        w?: number;
        h?: number;
        labelW?: number;
        labelRect?: { x: number; y: number; w: number; h: number };
        textIndent?: number;
        lastLineCutoff?: number;
        lineHeight?: number;
        deleted?: boolean;
      }
    > = JSON.parse(raw);
    return fields
      .filter((f) => !overrides[f.id]?.deleted)
      .map((f) => {
        const ov = overrides[f.id];
        if (!ov) return f;
        return {
          ...f,
          x: ov.x ?? f.x,
          y: ov.y ?? f.y,
          w: ov.w ?? f.w,
          h: ov.h ?? f.h,
          labelW: ov.labelW ?? f.labelW,
          labelRect: ov.labelRect ?? f.labelRect,
          textIndent: ov.textIndent ?? f.textIndent,
          lastLineCutoff: ov.lastLineCutoff ?? f.lastLineCutoff,
          lineHeight: ov.lineHeight ?? f.lineHeight,
        };
      });
  } catch {
    return fields;
  }
}

const MAX_ZOOM = 2.5;
const ZOOM_STEP = 0.1;

interface PdfFormProps {
  caseId: string;
  fields: FieldOverlay[];
  initialEventId?: string;
  /** If set, only pages up to and including this number are shown. */
  maxPage?: number;
}

interface PageState {
  cssW: number;
  cssH: number;
}

// SVG background — injects the raw SVG HTML once via ref callback, memo skips
// re-renders from parent state changes (optimistic, editing, etc).
const MemoSvgBackground = memo(function SvgBackground({
  pageNum,
  width,
  height,
  isDark,
}: {
  pageNum: 1 | 2;
  width: number;
  height: number;
  isDark: boolean;
}) {
  const prevRef = useRef<{ pageNum: 1 | 2; isDark: boolean } | null>(null);
  const refCb = useCallback(
    (el: HTMLDivElement | null) => {
      if (!el) return;
      const prev = prevRef.current;
      if (prev && prev.pageNum === pageNum && prev.isDark === isDark) return;
      const svgs = isDark
        ? [page1DarkSvg, page2DarkSvg]
        : [page1Svg, page2Svg];
      el.innerHTML = pageNum === 1 ? svgs[0] : svgs[1];
      prevRef.current = { pageNum, isDark };
    },
    [pageNum, isDark],
  );

  return (
    <div
      ref={refCb}
      style={{
        width,
        height,
        position: "relative",
        zIndex: 0,
        pointerEvents: "none",
        userSelect: "none",
        fontVariantNumeric: "normal",
      }}
    />
  );
});

export function PdfForm({ caseId, fields, initialEventId, maxPage }: PdfFormProps) {
  const { t } = useI18n();
  const { user } = useAuth();
  const { isAnon, anonName, anonDob, anonHospital, anonUnit } = useAnon();
  const dispatch = useCommand();
  const rpc = useRpc();
  const navigate = useNavigate();
  const { syncEntity } = useStore();
  const colors = useColors();
  const styles = useStyles();
  const { theme } = useTheme();
  const isDark = theme === "dark";

  const { data: unitUsageMap } = useQuery(async () => {
    if (!user?.userId) return new Map<string, number>();
    const sq = getSqlocal();
    const rows = await sq.sql`SELECT unit_id, last_used_at FROM medic_unit_usage WHERE user_id = ${user.userId}`;
    return new Map(rows.map((r) => [r.unit_id as string, r.last_used_at as number]));
  }, [user?.userId]);

  const { data: myAssignedUnitIds } = useQuery(async () => {
    if (!user?.userId) return [];
    const rows = await db.select({ unitId: s.userUnits.unitId }).from(s.userUnits).where(eq(s.userUnits.userId, user.userId)).all();
    return rows.map((r) => r.unitId);
  }, [user?.userId]);

  const containerRef = useRef<HTMLDivElement>(null);
  const formPaneRef = useRef<HTMLDivElement>(null);
  const pagesRef = useRef<HTMLDivElement>(null);
  const spacerRef = useRef<HTMLDivElement>(null);
  // zoom=1 means "fit to container width"; >1 zooms in, <1 zooms out
  const [zoom, setZoom] = useState(() => {
    const saved = localStorage.getItem("pdfForm.zoom");
    return saved ? Math.max(0.5, Math.min(MAX_ZOOM, Number(saved) || 1)) : 1;
  });
  const [searchParams] = useSearchParams();
  const [debugMode, setDebugMode] = useState(() => searchParams.has("debug"));
  const [sideBySide, setSideBySide] = useState(
    () => localStorage.getItem("pdfForm.sideBySide") === "1",
  );
  const [containerWidth, setContainerWidth] = useState(0);
  const [containerHeight, setContainerHeight] = useState(0);

  // Apply editor overrides in dev/debug mode
  const resolvedFields = useMemo(
    () =>
      import.meta.env.DEV || debugMode ? applyEditorOverrides(fields) : fields,
    [fields, debugMode],
  );

  // History panel state
  const [historyOpen, setHistoryOpen] = useState(!!initialEventId);
  // History cursor: -1 = live (current state), >=0 = viewing past event at that ASC index
  const [historyCursor, setHistoryCursor] = useState(-1);
  const ttInitialResolved = useRef(false);
  // Derived: viewing a past state when history is open and cursor is set
  const viewingPast = historyOpen && historyCursor >= 0;

  // Optimistic UI: immediate visual feedback before async data round-trip
  const [optimistic, setOptimistic] = useState<Map<string, unknown>>(new Map());

  // Popover editing: which field is being edited (null = none)
  const [editingFieldId, _setEditingFieldId] = useState<string | null>(null);

  // Help mode: when on, clicking a field (or a section overlay) shows its
  // explanation in the help sidebar instead of opening the editor.
  const [helpMode, setHelpMode] = useState(false);
  const [helpSection, setHelpSection] = useState<string | null>(null);

  const setEditingFieldId = useCallback<typeof _setEditingFieldId>(
    (value) => {
      if (typeof value === "function" || value === null) {
        _setEditingFieldId(value as string | null);
        return;
      }
      if (helpMode) {
        // In help mode, clicks on fields are swallowed — users open help by
        // tapping the explicit `?` buttons anchored to each numbered section.
        return;
      }
      _setEditingFieldId(value);
    },
    [helpMode],
  );

  // Body diagram popover state
  const [bodyDiagramOpen, setBodyDiagramOpen] = useState(false);

  // PDF export state
  const [exporting, setExporting] = useState(false);

  // Drag-to-scroll state
  const dragRef = useRef<{
    startX: number;
    startY: number;
    scrollX: number;
    scrollY: number;
  } | null>(null);
  const [dragging, setDragging] = useState(false);
  const zoomRef = useRef(zoom);
  useEffect(() => {
    zoomRef.current = zoom;
  }, [zoom]);

  // Track form pane size via ResizeObserver (clientWidth/Height exclude scrollbar)
  useEffect(() => {
    const el = formPaneRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => {
      setContainerWidth(el.clientWidth);
      setContainerHeight(el.clientHeight);
    });
    ro.observe(el);
    setContainerWidth(el.clientWidth);
    setContainerHeight(el.clientHeight);
    return () => ro.disconnect();
  }, []);

  // Compute page dimensions at base zoom (zoom=1 = fit to pane width)
  const availW = Math.max(200, containerWidth);
  const baseW = sideBySide ? availW / 2 : availW;
  const baseH = baseW * ASPECT;
  const pageState: PageState = useMemo(
    () => ({ cssW: baseW, cssH: baseH }),
    [baseW, baseH],
  );

  // Total content dimensions at zoom=1
  const totalBaseW = sideBySide ? baseW * 2 : baseW;
  const totalBaseH = sideBySide ? baseH : baseH * 2;

  // Dynamic min zoom: both pages fit entirely on screen (min 50%)
  const minZoom =
    containerHeight > 0
      ? Math.max(0.5, Math.min(1, containerHeight / totalBaseH))
      : 0.5;

  // Clamp zoom when minZoom changes (e.g. container resized)
  useEffect(() => {
    setZoom((z) => (z < minZoom ? minZoom : z));
  }, [minZoom]);

  // Centering offsets: when zoomed content is smaller than container
  const zoomedW = totalBaseW * zoom;
  const zoomedH = totalBaseH * zoom;
  const offsetX = Math.max(0, (containerWidth - zoomedW) / 2);
  const offsetY = 0;

  // Refs for values needed in pinch/wheel handler closures
  const baseWRef = useRef(baseW);
  useEffect(() => {
    baseWRef.current = baseW;
  }, [baseW]);
  const baseHRef = useRef(baseH);
  useEffect(() => {
    baseHRef.current = baseH;
  }, [baseH]);
  const sideBySideRef = useRef(sideBySide);
  useEffect(() => {
    sideBySideRef.current = sideBySide;
  }, [sideBySide]);
  const minZoomRef = useRef(minZoom);
  useEffect(() => {
    minZoomRef.current = minZoom;
  }, [minZoom]);
  const containerWidthRef = useRef(containerWidth);
  useEffect(() => {
    containerWidthRef.current = containerWidth;
  }, [containerWidth]);
  const containerHeightRef = useRef(containerHeight);
  useEffect(() => {
    containerHeightRef.current = containerHeight;
  }, [containerHeight]);

  // Debug mode toggle: Ctrl+Shift+D
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.ctrlKey && e.shiftKey && e.key === "D") {
        e.preventDefault();
        setDebugMode((prev) => !prev);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  // Load all case data
  const { data, loading } = useQuery(async () => {
    if (!caseId) return null;
    const c = await db.select().from(s.cases).where(eq(s.cases.id, caseId));
    if (!c[0]) return null;
    const [
      p,
      patientCases,
      vitals,
      ventilation,
      diagnostics,
      labValues,
      exam,
      cpr,
      tourniquets,
      medications,
      surgeries,
      atts,
      hospitals,
      units,
    ] = await Promise.all([
      db.select().from(s.patients).where(eq(s.patients.id, c[0].patientId)),
      c[0].patientId
        ? db
            .select({ id: s.cases.id })
            .from(s.cases)
            .where(eq(s.cases.patientId, c[0].patientId))
        : Promise.resolve([]),
      db.select().from(s.caseVitals).where(eq(s.caseVitals.caseId, caseId)),
      db
        .select()
        .from(s.caseVentilation)
        .where(eq(s.caseVentilation.caseId, caseId)),
      db
        .select()
        .from(s.caseDiagnostics)
        .where(eq(s.caseDiagnostics.caseId, caseId)),
      db
        .select()
        .from(s.caseLabValues)
        .where(eq(s.caseLabValues.caseId, caseId)),
      db.select().from(s.caseExam).where(eq(s.caseExam.caseId, caseId)),
      db.select().from(s.caseCpr).where(eq(s.caseCpr.caseId, caseId)),
      db
        .select()
        .from(s.caseTourniquet)
        .where(eq(s.caseTourniquet.caseId, caseId)),
      db
        .select()
        .from(s.caseMedication)
        .where(eq(s.caseMedication.caseId, caseId)),
      db.select().from(s.caseSurgery).where(eq(s.caseSurgery.caseId, caseId)),
      db.select().from(s.attachments).where(eq(s.attachments.caseId, caseId)),
      db.select().from(s.hospitals).where(eq(s.hospitals.archived, 0)),
      db.select().from(s.units).where(eq(s.units.archived, 0)),
    ]);
    return {
      case: c[0],
      patient: p[0] || null,
      patientCaseCount: patientCases.length,
      vitals,
      ventilation,
      diagnostics,
      labValues,
      exam,
      cpr,
      tourniquets,
      medications,
      surgeries,
      hospitals,
      units,
      attachments: atts.filter((a) => !a.deleted),
    };
  }, [caseId]);

  // Load case history (all events related to this case + child entities)
  // Sorted ASC for replay; reversed for display
  const needHistory = historyOpen;
  const { data: historyDataAsc, loading: historyLoading } =
    useQuery(async (): Promise<HistoryEntry[]> => {
      if (!caseId || !needHistory) return [];
      // Gather all entity IDs: case itself + all child table row IDs
      const entityIds = [caseId];
      // Also include patient ID (events target patient entity)
      if (data?.patient?.id) entityIds.push(data.patient.id);
      // Collect child entity IDs by scanning event log for create commands with caseUuid
      const childTables = [
        s.caseVitals,
        s.caseVentilation,
        s.caseDiagnostics,
        s.caseLabValues,
        s.caseExam,
        s.caseCpr,
        s.caseTourniquet,
        s.caseMedication,
        s.caseSurgery,
        s.attachments,
      ] as const;
      const childResults = await Promise.all(
        childTables.map((tbl) =>
          db.select({ id: tbl.id }).from(tbl).where(eq(tbl.caseId, caseId)),
        ),
      );
      for (const rows of childResults) {
        for (const r of rows) entityIds.push(r.id);
      }
      // Query events for all relevant entity IDs, ASC for replay
      const evts = await db
        .select()
        .from(s.events)
        .where(inArray(s.events.entityId, entityIds))
        .orderBy(s.events.clientTimestamp)
        .limit(2000);
      return evts.map((e) => ({
        id: e.id,
        type: e.type,
        payload: (typeof e.payload === "string"
          ? JSON.parse(e.payload)
          : e.payload) as Record<string, unknown>,
        userId: e.userId,
        clientTimestamp: e.clientTimestamp,
        serverTimestamp: e.serverTimestamp,
      }));
    }, [caseId, needHistory, data?.patient?.id]);

  // Load user names for history display
  const { data: userMap } = useQuery(async (): Promise<
    Record<string, string>
  > => {
    if (!historyDataAsc || historyDataAsc.length === 0) return {};
    const uids = [...new Set(historyDataAsc.map((e) => e.userId))];
    if (uids.length === 0) return {};
    const users = await db
      .select({ id: s.users.id, name: s.users.name, email: s.users.email })
      .from(s.users)
      .where(inArray(s.users.id, uids));
    const m: Record<string, string> = {};
    for (const u of users) m[u.id] = u.name || u.email || u.id.slice(0, 8);
    return m;
  }, [historyDataAsc]);

  // Reversed copy for history panel display (newest first)
  const historyData = historyDataAsc ? [...historyDataAsc].reverse() : null;

  // Replay snapshot: rebuild data from events up to historyCursor
  const ttData =
    viewingPast && historyDataAsc && historyDataAsc.length > 0
      ? replayEvents(
          historyDataAsc,
          historyCursor >= 0 ? historyCursor : historyDataAsc.length - 1,
          data?.hospitals || [],
          data?.units || [],
        )
      : null;

  // Resolve initialEventId to an index once history loads
  useEffect(() => {
    if (
      initialEventId &&
      historyDataAsc &&
      historyDataAsc.length > 0 &&
      !ttInitialResolved.current
    ) {
      const idx = historyDataAsc.findIndex((e) => e.id === initialEventId);
      if (idx >= 0) {
        setHistoryCursor(idx);
        ttInitialResolved.current = true;
      }
    }
  }, [initialEventId, historyDataAsc]);

  // The data object overlays should read from: live data or replayed snapshot
  const overlayData = viewingPast && ttData ? ttData : data;

  // Optimistic overrides are cleared per-mutation when the server acks the command.
  // See the ackPromise.then() in onChange above.

  // Zoom via ctrl+wheel, anchored to cursor position
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      if (!(e.ctrlKey || e.metaKey)) return;
      e.preventDefault();
      const scrollEl = formPaneRef.current;
      if (!scrollEl) return;
      const oldZoom = zoomRef.current;
      const mz = minZoomRef.current;
      const newZoom = Math.min(
        MAX_ZOOM,
        Math.max(mz, oldZoom - e.deltaY * 0.002),
      );
      if (newZoom === oldZoom) return;

      const bw = baseWRef.current;
      const bh = baseHRef.current;
      const sbs = sideBySideRef.current;
      const cw = containerWidthRef.current;
      const ch = containerHeightRef.current;
      const tbw = sbs ? bw * 2 : bw;
      const tbh = sbs ? bh : bh * 2;

      // Old centering offset (horizontal only)
      const oldZW = tbw * oldZoom;
      const oldOffX = Math.max(0, (cw - oldZW) / 2);
      const oldOffY = 0;

      // Mouse position relative to the scroll container
      const rect = scrollEl.getBoundingClientRect();
      const vpX = e.clientX - rect.left;
      const vpY = e.clientY - rect.top;
      // Content coordinate under the cursor at old zoom (clamped to content bounds)
      const contentX = Math.max(
        0,
        Math.min(tbw, (scrollEl.scrollLeft + vpX - oldOffX) / oldZoom),
      );
      const contentY = Math.max(
        0,
        Math.min(tbh, (scrollEl.scrollTop + vpY - oldOffY) / oldZoom),
      );

      // New centering offset (horizontal only)
      const newZW = tbw * newZoom;
      const newOffX = Math.max(0, (cw - newZW) / 2);
      const newOffY = 0;

      // Update spacer + pages directly for instant visual feedback
      if (spacerRef.current) {
        spacerRef.current.style.width = `${newZW}px`;
        spacerRef.current.style.height = `${tbh * newZoom}px`;
      }
      if (pagesRef.current)
        pagesRef.current.style.transform = `translate(${newOffX}px, ${newOffY}px) scale(${newZoom})`;
      // Scroll so the same content point stays under the cursor, clamped to valid bounds
      const maxScrollX = Math.max(0, newZW - cw);
      const maxScrollY = Math.max(0, tbh * newZoom - ch);
      scrollEl.scrollLeft = Math.max(
        0,
        Math.min(maxScrollX, contentX * newZoom + newOffX - vpX),
      );
      scrollEl.scrollTop = Math.max(
        0,
        Math.min(maxScrollY, contentY * newZoom + newOffY - vpY),
      );
      setZoom(newZoom);
      // Re-clamp after React re-renders to ensure bounds are respected
      requestAnimationFrame(() => {
        if (scrollEl.scrollLeft > maxScrollX) scrollEl.scrollLeft = maxScrollX;
        if (scrollEl.scrollTop > maxScrollY) scrollEl.scrollTop = maxScrollY;
      });
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, []);

  // Persist zoom to localStorage
  useEffect(() => {
    localStorage.setItem("pdfForm.zoom", String(zoom));
  }, [zoom]);

  // Persist scroll position (as fraction of scrollable area) and restore on mount
  const scrollRestoredRef = useRef(false);
  useEffect(() => {
    const el = formPaneRef.current;
    if (!el) return;
    // Restore once after layout settles
    if (!scrollRestoredRef.current && el.scrollHeight > el.clientHeight) {
      scrollRestoredRef.current = true;
      try {
        const saved = JSON.parse(
          localStorage.getItem("pdfForm.scroll") || "{}",
        );
        if (saved.fracX != null)
          el.scrollLeft = saved.fracX * (el.scrollWidth - el.clientWidth);
        if (saved.fracY != null)
          el.scrollTop = saved.fracY * (el.scrollHeight - el.clientHeight);
      } catch {
        /* ignore */
      }
    }
    let timer: ReturnType<typeof setTimeout> | null = null;
    const onScroll = () => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        const maxX = Math.max(0, el.scrollWidth - el.clientWidth);
        const maxY = Math.max(0, el.scrollHeight - el.clientHeight);
        localStorage.setItem(
          "pdfForm.scroll",
          JSON.stringify({
            fracX: maxX > 0 ? el.scrollLeft / maxX : 0,
            fracY: maxY > 0 ? el.scrollTop / maxY : 0,
          }),
        );
      }, 200);
    };
    el.addEventListener("scroll", onScroll);
    return () => {
      el.removeEventListener("scroll", onScroll);
      if (timer) clearTimeout(timer);
    };
  });

  // Drag-to-scroll (mouse only) + pinch-to-zoom (touch)
  useEffect(() => {
    const el = formPaneRef.current;
    if (!el) return;

    // Drag-to-scroll with inertia — works from anywhere on the form.
    // A small movement threshold distinguishes drags from taps so clicks
    // on form fields still fire normally.
    const DRAG_THRESHOLD = 5; // px
    let velX = 0,
      velY = 0,
      lastMoveTime = 0,
      inertiaRaf = 0;
    let pending: {
      x: number;
      y: number;
      scrollX: number;
      scrollY: number;
      pointerId: number;
    } | null = null;
    let committed = false;
    let pinching = false; // true while a 2-finger pinch is active

    const stopInertia = () => {
      if (inertiaRaf) {
        cancelAnimationFrame(inertiaRaf);
        inertiaRaf = 0;
      }
    };

    const onDown = (e: PointerEvent) => {
      if (e.button !== 0 || pinching) return;
      stopInertia();
      // Don't capture yet — just record start position
      pending = {
        x: e.clientX,
        y: e.clientY,
        scrollX: el.scrollLeft,
        scrollY: el.scrollTop,
        pointerId: e.pointerId,
      };
      committed = false;
      velX = 0;
      velY = 0;
      lastMoveTime = performance.now();
    };

    const onMove = (e: PointerEvent) => {
      if (!pending || pinching) return;
      const dx = e.clientX - pending.x;
      const dy = e.clientY - pending.y;

      if (!committed) {
        if (Math.abs(dx) < DRAG_THRESHOLD && Math.abs(dy) < DRAG_THRESHOLD)
          return;
        // Exceeded threshold — commit to drag
        committed = true;
        dragRef.current = {
          startX: pending.x,
          startY: pending.y,
          scrollX: pending.scrollX,
          scrollY: pending.scrollY,
        };
        setDragging(true);
        el.setPointerCapture(pending.pointerId);
      }

      if (!dragRef.current) return;
      const now = performance.now();
      const dt = now - lastMoveTime;
      const newScrollX = dragRef.current.scrollX - dx;
      const newScrollY = dragRef.current.scrollY - dy;
      if (dt > 0) {
        const ivx = (newScrollX - el.scrollLeft) / dt;
        const ivy = (newScrollY - el.scrollTop) / dt;
        velX = velX * 0.4 + ivx * 0.6;
        velY = velY * 0.4 + ivy * 0.6;
      }
      lastMoveTime = now;
      el.scrollLeft = newScrollX;
      el.scrollTop = newScrollY;
    };

    const onUp = (e: PointerEvent) => {
      if (!pending) return;
      const wasDrag = committed;
      pending = null;
      if (!wasDrag) return; // Was a tap — let the click event fire normally
      dragRef.current = null;
      setDragging(false);
      el.releasePointerCapture(e.pointerId);
      // Inertia: apply decaying velocity
      const friction = 0.95;
      let prevTime = performance.now();
      const tick = () => {
        const now = performance.now();
        const dt = now - prevTime;
        prevTime = now;
        velX *= Math.pow(friction, dt / 16);
        velY *= Math.pow(friction, dt / 16);
        if (Math.abs(velX) < 0.01 && Math.abs(velY) < 0.01) {
          inertiaRaf = 0;
          return;
        }
        el.scrollLeft += velX * dt;
        el.scrollTop += velY * dt;
        inertiaRaf = requestAnimationFrame(tick);
      };
      if (Math.abs(velX) > 0.05 || Math.abs(velY) > 0.05) {
        inertiaRaf = requestAnimationFrame(tick);
      }
    };

    el.addEventListener("pointerdown", onDown);
    el.addEventListener("pointermove", onMove);
    el.addEventListener("pointerup", onUp);
    el.addEventListener("pointercancel", onUp);

    // Suppress click after a drag so releasing doesn't toggle form fields
    const onClick = (e: MouseEvent) => {
      if (committed) {
        e.stopPropagation();
        e.preventDefault();
      }
    };
    el.addEventListener("click", onClick, true);

    // Touch: 2-finger pinch-to-zoom centered on midpoint (GPU-accelerated via CSS transform)
    let pinchStartDist = 0;
    let pinchPrevDist = 0;
    let pinchLastMidX = 0;
    let pinchLastMidY = 0;
    let pinchLastZoom = 1;

    const onTouchStart = (e: TouchEvent) => {
      if (e.touches.length === 2) {
        e.preventDefault();
        // Cancel any in-progress drag
        pinching = true;
        if (pending) {
          if (committed) {
            el.releasePointerCapture(pending.pointerId);
            dragRef.current = null;
            setDragging(false);
          }
          pending = null;
          committed = false;
        }
        const dx = e.touches[0].clientX - e.touches[1].clientX;
        const dy = e.touches[0].clientY - e.touches[1].clientY;
        pinchStartDist = Math.hypot(dx, dy);
        pinchPrevDist = pinchStartDist;
        pinchLastZoom = zoomRef.current;
        pinchLastMidX = (e.touches[0].clientX + e.touches[1].clientX) / 2;
        pinchLastMidY = (e.touches[0].clientY + e.touches[1].clientY) / 2;
      }
    };

    const onTouchMove = (e: TouchEvent) => {
      if (e.touches.length === 2 && pinchStartDist > 0) {
        e.preventDefault();
        const dx = e.touches[0].clientX - e.touches[1].clientX;
        const dy = e.touches[0].clientY - e.touches[1].clientY;
        const dist = Math.hypot(dx, dy);
        if (pinchPrevDist === 0) {
          pinchPrevDist = dist;
          return;
        }
        const scale = dist / pinchPrevDist;
        pinchPrevDist = dist;
        const mz = minZoomRef.current;
        const newZoom = Math.min(MAX_ZOOM, Math.max(mz, pinchLastZoom * scale));

        const bw = baseWRef.current;
        const bh = baseHRef.current;
        const sbs = sideBySideRef.current;
        const cw = containerWidthRef.current;
        const ch = containerHeightRef.current;
        const tbw = sbs ? bw * 2 : bw;
        const tbh = sbs ? bh : bh * 2;

        // Old centering offset (horizontal only)
        const oldZW = tbw * pinchLastZoom;
        const oldOffX = Math.max(0, (cw - oldZW) / 2);
        const oldOffY = 0;

        const midX = (e.touches[0].clientX + e.touches[1].clientX) / 2;
        const midY = (e.touches[0].clientY + e.touches[1].clientY) / 2;

        // Midpoint position within the scroll container (viewport-relative)
        const rect = el.getBoundingClientRect();
        const vpX = midX - rect.left;
        const vpY = midY - rect.top;

        // Content coordinate under the midpoint at old zoom
        const contentX = (el.scrollLeft + vpX - oldOffX) / pinchLastZoom;
        const contentY = (el.scrollTop + vpY - oldOffY) / pinchLastZoom;

        // New centering offset (horizontal only)
        const newZW = tbw * newZoom;
        const newOffX = Math.max(0, (cw - newZW) / 2);
        const newOffY = 0;

        // Update spacer dimensions so scroll area matches new zoom
        if (spacerRef.current) {
          spacerRef.current.style.width = `${newZW}px`;
          spacerRef.current.style.height = `${tbh * newZoom}px`;
        }

        // Apply transform directly to DOM for 60fps (no React re-render)
        if (pagesRef.current) {
          pagesRef.current.style.transform = `translate(${newOffX}px, ${newOffY}px) scale(${newZoom})`;
        }

        // Scroll so the same content point stays under the midpoint, clamped to valid bounds
        const maxScrollX = Math.max(0, newZW - cw);
        const maxScrollY = Math.max(0, tbh * newZoom - ch);
        el.scrollLeft = Math.max(
          0,
          Math.min(
            maxScrollX,
            contentX * newZoom + newOffX - vpX - (midX - pinchLastMidX),
          ),
        );
        el.scrollTop = Math.max(
          0,
          Math.min(
            maxScrollY,
            contentY * newZoom + newOffY - vpY - (midY - pinchLastMidY),
          ),
        );

        pinchLastMidX = midX;
        pinchLastMidY = midY;
        pinchLastZoom = newZoom;
      }
    };

    const onTouchEnd = (e: TouchEvent) => {
      if (pinchStartDist > 0) {
        // Commit the final zoom to React state (single re-render)
        setZoom(pinchLastZoom);
      }
      pinchStartDist = 0;
      // Only clear pinching when all fingers are lifted
      if (e.touches.length === 0) pinching = false;
    };

    el.addEventListener("touchstart", onTouchStart, { passive: false });
    el.addEventListener("touchmove", onTouchMove, { passive: false });
    el.addEventListener("touchend", onTouchEnd);
    el.addEventListener("touchcancel", onTouchEnd);

    return () => {
      stopInertia();
      el.removeEventListener("pointerdown", onDown);
      el.removeEventListener("pointermove", onMove);
      el.removeEventListener("pointerup", onUp);
      el.removeEventListener("pointercancel", onUp);
      el.removeEventListener("click", onClick, true);
      el.removeEventListener("touchstart", onTouchStart);
      el.removeEventListener("touchmove", onTouchMove);
      el.removeEventListener("touchend", onTouchEnd);
      el.removeEventListener("touchcancel", onTouchEnd);
    };
  }, []);

  const fire = useCallback(
    async (
      cmd: string,
      payload: Record<string, unknown>,
    ): Promise<{ ack: Promise<void> }> =>
      dispatch(cmd as CommandType, payload as any),
    [dispatch],
  );

  /** View the form as it was at a specific event */
  const goToEvent = useCallback(
    (evt: HistoryEntry, idx: number) => {
      setHistoryCursor(idx);
      navigate(`/cases/${caseId}/history/${evt.id}`, { replace: true });
    },
    [caseId, navigate],
  );

  const buildOverlay = (pageNum: 1 | 2, ps: PageState) => {
    if (!overlayData) return null;
    const pageFields = resolvedFields.filter((f) => f.page === pageNum);
    return pageFields.map((field) => {
      const left = (field.x * ps.cssW) / 100;
      const width = (field.w * ps.cssW) / 100;
      const height = (field.h * ps.cssH) / 100;
      // SVG coordinates: field.y is the underline position.
      // For single-line text we position so the CSS baseline lands on y:
      //   top + h * BASELINE_FRAC = y  →  top = y − h * BASELINE_FRAC
      // Textareas: field.y is the first-line baseline.
      // Checkboxes/radios: y is the rect top, no offset needed.
      const isSingleLine =
        field.type !== "textarea" && field.type !== "patient-name";
      const isTopAligned =
        (field.type as string) === "checkbox" ||
        (field.type as string) === "radio";
      const top = isTopAligned
        ? (field.y * ps.cssH) / 100
        : ((field.y - field.h * BASELINE_FRAC) * ps.cssH) / 100;

      const dataValue = field.getValue(overlayData);
      const value = optimistic.has(field.id)
        ? optimistic.get(field.id)
        : dataValue;
      // PII fields that should be redacted and locked in anonymous mode
      const isAnonPii = isAnon && !!field.isPii;
      const onChange = async (val: unknown) => {
        if (viewingPast) return; // read-only when viewing past state
        // Collect all field IDs affected by this change
        const affectedIds = [field.id];
        // Optimistic: update local state immediately for instant feedback
        setOptimistic((prev) => {
          const next = new Map(prev).set(field.id, val);
          // Radio-like exclusive group: when checking one, uncheck siblings
          if (field.radioGroup && val === true) {
            for (const sib of resolvedFields) {
              if (sib.radioGroup === field.radioGroup && sib.id !== field.id) {
                next.set(sib.id, false);
                affectedIds.push(sib.id);
              }
            }
          }
          // Linked date/time fields: sync the paired field (time↔datetime-date)
          const linkedType =
            field.type === "time"
              ? "datetime-date"
              : field.type === "datetime-date"
                ? "time"
                : null;
          if (linkedType) {
            for (const sib of resolvedFields) {
              if (sib.type === linkedType && sib.command === field.command) {
                next.set(sib.id, val);
                affectedIds.push(sib.id);
              }
            }
          }
          return next;
        });
        let payload = field.getPayload(val, data);
        if ((!payload || !(payload as any).uuid) && field.autoCreate) {
          // Auto-create the parent row, then retry getPayload
          const newUuid = uuidv7();
          const { ack: autoAck } = await fire(field.autoCreate.command, {
            ...field.autoCreate.getPayload(caseId),
            uuid: newUuid,
          });
          autoAck.catch(() => {});
          payload = field.getPayload(val, data, newUuid);
        }
        // Auto-provision time field when editing a non-time vitals/vent field
        const rowPrefix = field.id.match(/^(p\d-(?:vitals|vent)-\d)/)?.[1];
        if (rowPrefix && field.type !== "time") {
          const timeFieldId = `${rowPrefix}-time`;
          const timeField = resolvedFields.find((f) => f.id === timeFieldId);
          if (timeField) {
            const timeVal = optimistic.has(timeFieldId)
              ? optimistic.get(timeFieldId)
              : timeField.getValue(overlayData);
            if (!timeVal) {
              const now = Date.now();
              setOptimistic((prev) => new Map(prev).set(timeFieldId, now));
              affectedIds.push(timeFieldId);
              const uuid = (payload as any)?.uuid;
              if (uuid) {
                fire(timeField.command, { uuid, timestamp: now });
              }
            }
          }
        }

        if (payload) {
          const { ack: ackPromise } = await fire(field.command, payload);
          // Clear optimistic overrides for this mutation once the server confirms
          ackPromise
            .then(() => {
              setOptimistic((prev) => {
                const next = new Map(prev);
                for (const id of affectedIds) next.delete(id);
                return next;
              });
            })
            .catch(() => {
              // On error, revert optimistic state (event already reverted by sync engine)
              setOptimistic((prev) => {
                const next = new Map(prev);
                for (const id of affectedIds) next.delete(id);
                return next;
              });
            });
        }
      };

      const fieldLabel = field.label || getFieldLabel(field.id, t);

      // Font size scales with the SVG: most form text is ~5.5px in the
      // 841.89-unit viewBox, so we match that proportionally.
      const scaledFont = (5.8 / SVG_H) * ps.cssH;

      // Shrink font to fit within the field height if the container is very narrow
      const fontSize = Math.min(scaledFont, height * 0.85);

      const baseStyle: React.CSSProperties = {
        position: "absolute",
        left,
        top,
        width,
        height,
        zIndex: 1,
        boxSizing: "border-box",
        fontSize: `${fontSize}px`,
        fontFamily: "'e-Ukraine', system-ui, sans-serif",
        fontWeight: 400,
        background: "transparent",
        border: debugMode ? "1px dashed red" : "none",
        padding: 0,
        textAlign: "center",
        color: colors.formInk,
        outline: "none",
        // For single-line inputs the box bottom is at the baseline; use
        // lineHeight = box height so text vertically centres within the
        // box — effectively sitting just above the underline.
        // For textareas, field.lineHeight (% of page) aligns text to printed underlines.
        lineHeight: isSingleLine
          ? `${height}px`
          : field.lineHeight
            ? `${(field.lineHeight * ps.cssH) / 100}px`
            : 1.2,
      };

      // Label hit zone for non-checkbox fields — makes the printed SVG label clickable
      const labelHitZone =
        field.labelRect && !viewingPast ? (
          <div
            title={fieldLabel}
            style={{
              position: "absolute",
              left: (field.labelRect.x * ps.cssW) / 100,
              top: (field.labelRect.y * ps.cssH) / 100,
              width: (field.labelRect.w * ps.cssW) / 100,
              height: (field.labelRect.h * ps.cssH) / 100,
              zIndex: 1,
              cursor: "pointer",
              background: "transparent",
              border: debugMode ? "1px dashed blue" : "none",
              boxSizing: "border-box",
            }}
            onClick={() => setEditingFieldId(field.id)}
          />
        ) : null;

      switch (field.type) {
        case "time":
        case "datetime-date": {
          const ts = value as number | null;
          const rowExists = rowExistsForField(field.id, overlayData);
          let displayVal = "";
          if (ts) {
            if (field.type === "time") {
              const dt2 = new Date(ts);
              displayVal = `${String(dt2.getHours()).padStart(2, "0")}:${String(dt2.getMinutes()).padStart(2, "0")}`;
            } else {
              // datetime-date: show dd.mm.yyyy
              const dt = new Date(ts);
              displayVal = `${String(dt.getDate()).padStart(2, "0")}.${String(dt.getMonth() + 1).padStart(2, "0")}.${dt.getFullYear()}`;
            }
          } else if (rowExists) {
            displayVal = "–";
          }

          return (
            <Fragment key={field.id}>
              {labelHitZone}
              <div
                title={fieldLabel}
                style={{
                  ...baseStyle,
                  cursor: viewingPast ? "default" : "pointer",
                  overflow: "hidden",
                  whiteSpace: "nowrap",
                  textOverflow: "ellipsis",
                }}
                onClick={() => {
                  if (!viewingPast) setEditingFieldId(field.id);
                }}
              >
                {displayVal}
              </div>
              {editingFieldId === field.id && (
                <DateTimeInputPopover
                  mode={field.type === "time" ? "time" : "datetime"}
                  timestamp={ts}
                  label={fieldLabel}
                  onCommit={(newTs: number | null) => onChange(newTs)}
                  onClose={() => setEditingFieldId(null)}
                  colors={colors}
                  t={t}
                  minTimestamp={field.minValue?.(overlayData)}
                />
              )}
            </Fragment>
          );
        }

        case "date": {
          const displayVal = isAnonPii ? "—" : fmtDateValue(value);
          return (
            <Fragment key={field.id}>
              {labelHitZone}
              <div
                title={fieldLabel}
                style={{
                  ...baseStyle,
                  cursor: viewingPast || isAnonPii ? "default" : "pointer",
                  overflow: "hidden",
                  whiteSpace: "nowrap",
                  textOverflow: "ellipsis",
                }}
                onClick={() => {
                  if (!viewingPast && !isAnonPii) setEditingFieldId(field.id);
                }}
              >
                {displayVal}
              </div>
              {editingFieldId === field.id && (
                <DateTimeInputPopover
                  mode="date"
                  dateStr={value as string | null}
                  label={fieldLabel}
                  onCommitDate={(d: string | null) => onChange(d)}
                  onClose={() => setEditingFieldId(null)}
                  colors={colors}
                  t={t}
                />
              )}
            </Fragment>
          );
        }

        case "text":
        case "number": {
          // Show dash if row exists but value is empty (for vitals/ventilation)
          const rowExists = rowExistsForField(field.id, overlayData);
          const displayVal = isAnonPii ? "—"
            : (value == null || value === "") && rowExists ? "–" : toStr(value);

          const onPopoverCommit = (v: string) => {
            if (field.type === "number") {
              const trimmed = v.trim().replace(",", ".");
              const num = Number(trimmed);
              onChange(trimmed === "" || isNaN(num) ? null : num);
            } else {
              onChange(v);
            }
          };

          return (
            <Fragment key={field.id}>
              {labelHitZone}
              <div
                title={fieldLabel}
                style={{
                  ...baseStyle,
                  cursor: viewingPast || isAnonPii ? "default" : "pointer",
                  textAlign: field.type === "text" ? "left" : "center",
                  overflow: field.type === "number" ? "visible" : "hidden",
                  whiteSpace: "nowrap",
                  textOverflow:
                    field.type === "number" ? undefined : "ellipsis",
                }}
                onClick={() => {
                  if (!viewingPast && !isAnonPii) setEditingFieldId(field.id);
                }}
              >
                {displayVal}
              </div>
              {editingFieldId === field.id && (
                <PopoverInput
                  value={toStr(value)}
                  fieldType={field.editAs || field.type}
                  label={fieldLabel}
                  onCommit={onPopoverCommit}
                  onClose={() => setEditingFieldId(null)}
                  colors={colors}
                />
              )}
            </Fragment>
          );
        }

        case "checkbox": {
          const lr = field.labelRect;
          if (lr) {
            // Full label rect: separate hit zone covering both checkbox + label
            const hitLeft = (lr.x * ps.cssW) / 100;
            const hitTop = (lr.y * ps.cssH) / 100;
            const hitW = (lr.w * ps.cssW) / 100;
            const hitH = (lr.h * ps.cssH) / 100;
            return (
              <Fragment key={field.id}>
                {/* Invisible hit zone covering the label rect */}
                <div
                  role="checkbox"
                  aria-checked={!!value}
                  tabIndex={0}
                  title={fieldLabel}
                  style={{
                    position: "absolute",
                    left: hitLeft,
                    top: hitTop,
                    width: hitW,
                    height: hitH,
                    zIndex: 2,
                    cursor: "pointer",
                    background: "transparent",
                    border: debugMode ? "1px dashed red" : "none",
                    boxSizing: "border-box",
                  }}
                  onClick={() => onChange(!value)}
                  onKeyDown={(e) => {
                    if (e.key === " " || e.key === "Enter") {
                      e.preventDefault();
                      onChange(!value);
                    }
                  }}
                />
                {/* Visible checkbox fill */}
                {!!value && (
                  <div
                    style={{
                      position: "absolute",
                      left,
                      top,
                      width,
                      height,
                      zIndex: 2,
                      pointerEvents: "none",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <div
                      style={{
                        width: "100%",
                        height: "100%",
                        background: colors.formInk,
                      }}
                    />
                  </div>
                )}
              </Fragment>
            );
          }
          // Fallback: old labelW style
          const hitWidth = field.labelW
            ? ((field.w + field.labelW) * ps.cssW) / 100
            : width;
          return (
            <div
              key={field.id}
              role="checkbox"
              aria-checked={!!value}
              tabIndex={0}
              title={fieldLabel}
              style={{
                ...baseStyle,
                zIndex: 2,
                width: hitWidth,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                padding: 0,
              }}
              onClick={() => onChange(!value)}
              onKeyDown={(e) => {
                if (e.key === " " || e.key === "Enter") {
                  e.preventDefault();
                  onChange(!value);
                }
              }}
            >
              <div
                style={{
                  width,
                  height: "100%",
                  flexShrink: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {!!value && (
                  <div
                    style={{
                      width: "100%",
                      height: "100%",
                      background: colors.formInk,
                    }}
                  />
                )}
              </div>
            </div>
          );
        }

        case "select": {
          const resolvedOptions = typeof field.options === "function" ? field.options(data) : (field.options || []);
          const selLabel =
            resolvedOptions.find((o: any) => o.value === value)?.label || "";
          return (
            <Fragment key={field.id}>
              {labelHitZone}
              <div
                title={fieldLabel}
                style={{
                  ...baseStyle,
                  cursor: viewingPast || isAnonPii ? "default" : "pointer",
                  overflow: "hidden",
                  whiteSpace: "nowrap",
                  textOverflow: "ellipsis",
                }}
                onClick={() => {
                  if (!viewingPast && !isAnonPii) setEditingFieldId(field.id);
                }}
              >
                {isAnonPii ? "—" : selLabel}
              </div>
              {editingFieldId === field.id && (
                <SelectPopover
                  value={String(value ?? "")}
                  options={resolvedOptions}
                  label={fieldLabel}
                  onCommit={(v) => onChange(v || null)}
                  onClose={() => setEditingFieldId(null)}
                  colors={colors}
                />
              )}
            </Fragment>
          );
        }

        case "hospital-select": {
          const hospitals = (data?.hospitals || []) as Array<{ id: string; name: string | null }>;
          const hospitalLabel = hospitals.find((h) => h.id === value)?.name || "";
          const hospitalOptions = [
            { value: "", label: "—" },
            ...hospitals.map((h) => ({ value: h.id, label: h.name || h.id })),
          ];
          return (
            <Fragment key={field.id}>
              {labelHitZone}
              <div
                title={fieldLabel}
                style={{
                  ...baseStyle,
                  cursor: viewingPast || isAnon ? "default" : "pointer",
                  overflow: "hidden",
                  whiteSpace: "nowrap",
                  textOverflow: "ellipsis",
                }}
                onClick={() => {
                  if (!viewingPast && !isAnon) setEditingFieldId(field.id);
                }}
              >
                {isAnon ? anonHospital(String(value ?? ""), hospitalLabel) : hospitalLabel}
              </div>
              {editingFieldId === field.id && (
                <SelectPopover
                  value={String(value ?? "")}
                  options={hospitalOptions}
                  label={fieldLabel}
                  onCommit={(v) => onChange(v || null)}
                  onClose={() => setEditingFieldId(null)}
                  colors={colors}
                />
              )}
            </Fragment>
          );
        }

        case "unit-autocomplete": {
          // value is { unitId, unitName } normally, or a raw UUID string during optimistic update
          const unitId = typeof value === "string" ? value : ((value as { unitId?: string } | null)?.unitId || "");
          const unitLabel = typeof value === "string"
            ? (data?.units?.find((u: any) => u.id === value)?.name || "")
            : ((value as { unitName?: string } | null)?.unitName || "");
          return (
            <Fragment key={field.id}>
              {labelHitZone}
              <div
                title={fieldLabel}
                style={{
                  ...baseStyle,
                  cursor: viewingPast || isAnon ? "default" : "pointer",
                  overflow: "hidden",
                  whiteSpace: "nowrap",
                  textOverflow: "ellipsis",
                }}
                onClick={() => {
                  if (!viewingPast && !isAnon) setEditingFieldId(field.id);
                }}
              >
                {isAnon ? anonUnit(unitId, unitLabel) : unitLabel}
              </div>
              {editingFieldId === field.id && (
                <UnitPopover
                  value={unitLabel}
                  label={fieldLabel}
                  onSelect={(unitId) => { onChange(unitId); setEditingFieldId(null); }}
                  onClear={() => { onChange(""); setEditingFieldId(null); }}
                  onClose={() => setEditingFieldId(null)}
                  colors={colors}
                />
              )}
            </Fragment>
          );
        }

        case "facility-autocomplete": {
          // value is { unitId, unitName } from getValue
          const facUnitId = typeof value === "string" ? value : (value as any)?.unitId || "";
          const facLabel = typeof value === "string"
            ? (overlayData?.units?.find((u: any) => u.id === value)?.name || value || "")
            : ((value as any)?.unitName || "");
          // Resolve the user's assigned units for the dropdown
          const myUnits = myAssignedUnitIds?.length
            ? (overlayData?.units || [])
                .filter((u: any) => myAssignedUnitIds.includes(u.id) && !u.archived && !u.mergedInto)
                .sort((a: any, b: any) => (unitUsageMap?.get(b.id) ?? 0) - (unitUsageMap?.get(a.id) ?? 0))
            : [];
          return (
            <Fragment key={field.id}>
              {labelHitZone}
              <div
                title={fieldLabel}
                style={{
                  ...baseStyle,
                  cursor: viewingPast || isAnon ? "default" : "pointer",
                  overflow: "hidden",
                  whiteSpace: "nowrap",
                  textOverflow: "ellipsis",
                }}
                onClick={() => { if (!viewingPast && !isAnon) setEditingFieldId(field.id); }}
              >
                {isAnon ? anonUnit(facUnitId, facLabel) : facLabel}
              </div>
              {editingFieldId === field.id && (
                <FacilityPopover
                  value={facUnitId}
                  label={fieldLabel}
                  userUnits={myUnits}
                  usageMap={unitUsageMap ?? undefined}
                  onSelect={(unitId) => { onChange(unitId); setEditingFieldId(null); }}
                  onClear={() => { onChange(""); setEditingFieldId(null); }}
                  onClose={() => setEditingFieldId(null)}
                  colors={colors}
                />
              )}
            </Fragment>
          );
        }

        case "blood-rh": {
          const selLabel =
            (field.options || []).find((o) => o.value === value)?.label || "";
          // Find the paired blood-rh field on the same page
          const group = resolvedFields.filter(
            (f) => f.type === "blood-rh" && f.page === field.page,
          );
          const btField = group.find(
            (f) => f.command === "patient.setBloodType",
          );
          const rhField = group.find(
            (f) => f.command === "patient.setRhFactor",
          );
          // Either field opening triggers the same popover
          const pairOpen =
            editingFieldId !== null &&
            group.some((f) => f.id === editingFieldId);
          const isFirst = field === group[0]; // only first field renders the popover
          return (
            <Fragment key={field.id}>
              {labelHitZone}
              <div
                title={fieldLabel}
                style={{
                  ...baseStyle,
                  cursor: viewingPast ? "default" : "pointer",
                  overflow: "hidden",
                  whiteSpace: "nowrap",
                  textOverflow: "ellipsis",
                }}
                onClick={() => {
                  if (!viewingPast) setEditingFieldId(field.id);
                }}
              >
                {selLabel}
              </div>
              {pairOpen && isFirst && btField && rhField && (
                <BloodRhPickerPopover
                  bloodType={
                    String(
                      (optimistic.has(btField.id)
                        ? optimistic.get(btField.id)
                        : btField.getValue(overlayData)) ?? "",
                    ) || null
                  }
                  rhFactor={
                    String(
                      (optimistic.has(rhField.id)
                        ? optimistic.get(rhField.id)
                        : rhField.getValue(overlayData)) ?? "",
                    ) || null
                  }
                  onCommit={async (bt, rh) => {
                    const ids = [btField.id, rhField.id];
                    setOptimistic((prev) => {
                      const next = new Map(prev);
                      next.set(btField.id, bt);
                      next.set(rhField.id, rh);
                      return next;
                    });
                    const acks: Promise<void>[] = [];
                    const btPayload = btField.getPayload(bt, data);
                    const rhPayload = rhField.getPayload(rh, data);
                    if (btPayload) {
                      const { ack } = await fire(btField.command, btPayload);
                      acks.push(ack);
                    }
                    if (rhPayload) {
                      const { ack } = await fire(rhField.command, rhPayload);
                      acks.push(ack);
                    }
                    Promise.all(acks).finally(() => {
                      setOptimistic((prev) => {
                        const next = new Map(prev);
                        for (const id of ids) next.delete(id);
                        return next;
                      });
                    });
                  }}
                  onClose={() => setEditingFieldId(null)}
                  colors={colors}
                />
              )}
            </Fragment>
          );
        }

        case "vision": {
          // Find the paired vision fields on the same page
          const group = resolvedFields.filter(
            (f) => f.type === "vision" && f.page === field.page,
          );
          const rightField = group.find(
            (f) => f.command === "exam.setVisionRight",
          );
          const leftField = group.find(
            (f) => f.command === "exam.setVisionLeft",
          );
          // Either field opening triggers the same popover
          const pairOpen =
            editingFieldId !== null &&
            group.some((f) => f.id === editingFieldId);
          const isFirst = field === group[0]; // only first field renders the popover
          return (
            <Fragment key={field.id}>
              {labelHitZone}
              <div
                title={fieldLabel}
                style={{
                  ...baseStyle,
                  cursor: viewingPast ? "default" : "pointer",
                  overflow: "hidden",
                  whiteSpace: "nowrap",
                  textOverflow: "ellipsis",
                }}
                onClick={() => {
                  if (!viewingPast) setEditingFieldId(field.id);
                }}
              >
                {toStr(value)}
              </div>
              {pairOpen && isFirst && rightField && leftField && (
                <VisionPickerPopover
                  visionR={
                    String(
                      (optimistic.has(rightField.id)
                        ? optimistic.get(rightField.id)
                        : rightField.getValue(overlayData)) ?? "",
                    ) || null
                  }
                  visionL={
                    String(
                      (optimistic.has(leftField.id)
                        ? optimistic.get(leftField.id)
                        : leftField.getValue(overlayData)) ?? "",
                    ) || null
                  }
                  onCommit={async (r, l) => {
                    const ids = [rightField.id, leftField.id];
                    setOptimistic((prev) => {
                      const next = new Map(prev);
                      next.set(rightField.id, r);
                      next.set(leftField.id, l);
                      return next;
                    });
                    const acks: Promise<void>[] = [];
                    let rPayload = rightField.getPayload(r, data);
                    let lPayload = leftField.getPayload(l, data);
                    // Auto-create exam row if needed
                    if (
                      (!rPayload || !(rPayload as any).uuid) &&
                      rightField.autoCreate
                    ) {
                      const newUuid = uuidv7();
                      const { ack: autoAck } = await fire(
                        rightField.autoCreate.command,
                        {
                          ...rightField.autoCreate.getPayload(caseId),
                          uuid: newUuid,
                        },
                      );
                      autoAck.catch(() => {});
                      rPayload = rightField.getPayload(r, data, newUuid);
                      lPayload = leftField.getPayload(l, data, newUuid);
                    }
                    if (rPayload) {
                      const { ack } = await fire(rightField.command, rPayload);
                      acks.push(ack);
                    }
                    if (lPayload) {
                      const { ack } = await fire(leftField.command, lPayload);
                      acks.push(ack);
                    }
                    Promise.all(acks).finally(() => {
                      setOptimistic((prev) => {
                        const next = new Map(prev);
                        for (const id of ids) next.delete(id);
                        return next;
                      });
                    });
                  }}
                  onClose={() => setEditingFieldId(null)}
                  colors={colors}
                />
              )}
            </Fragment>
          );
        }

        case "textarea": {
          const cutoffW = field.lastLineCutoff
            ? (field.lastLineCutoff * ps.cssW) / 100
            : 0;
          const lhPx = field.lineHeight
            ? (field.lineHeight * ps.cssH) / 100
            : fontSize * 1.2;
          return (
            <Fragment key={field.id}>
              {labelHitZone}
              <div
                title={fieldLabel}
                style={{
                  ...baseStyle,
                  cursor: viewingPast ? "default" : "pointer",
                  textAlign: "left",
                  overflow: "hidden",
                  display: "-webkit-box",
                  WebkitBoxOrient: "vertical",
                  WebkitLineClamp: Math.max(1, Math.floor(height / lhPx)),
                  wordBreak: "break-word",
                  textIndent: field.textIndent
                    ? (field.textIndent * ps.cssW) / 100
                    : undefined,
                }}
                onClick={() => {
                  if (!viewingPast) setEditingFieldId(field.id);
                }}
              >
                {cutoffW > 0 && (
                  <div
                    style={{
                      float: "right",
                      width: cutoffW,
                      height: "100%",
                      shapeOutside: `inset(calc(100% - ${lhPx}px) 0 0 0)`,
                    }}
                  />
                )}
                {toStr(value)}
              </div>
              {editingFieldId === field.id && (
                <PopoverInput
                  value={toStr(value)}
                  fieldType={field.editAs || "textarea"}
                  label={fieldLabel}
                  onCommit={(v) => onChange(v)}
                  onClose={() => setEditingFieldId(null)}
                  colors={colors}
                />
              )}
            </Fragment>
          );
        }

        case "signature": {
          const sha256 = value as string | null;
          return (
            <Fragment key={field.id}>
              {labelHitZone}
              <div
                title={fieldLabel}
                style={{
                  ...baseStyle,
                  cursor: viewingPast ? "default" : "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  overflow: "hidden",
                }}
                onClick={() => {
                  if (!viewingPast) setEditingFieldId(field.id);
                }}
              >
                {sha256 ? (
                  <SignatureThumbnail sha256={sha256} invert={isDark} />
                ) : (
                  <span
                    style={{
                      color: colors.textMuted,
                      fontSize: "0.7em",
                      fontStyle: "italic",
                    }}
                  >
                    [{fieldLabel}]
                  </span>
                )}
              </div>
              {editingFieldId === field.id && (
                <SignaturePopover
                  sha256={sha256}
                  label={fieldLabel}
                  onCommit={async (newSha256) => {
                    setEditingFieldId(null);
                    if (newSha256 !== sha256) await onChange(newSha256);
                  }}
                  onClose={() => setEditingFieldId(null)}
                  colors={colors}
                />
              )}
            </Fragment>
          );
        }

        case "patient-name": {
          return (
            <Fragment key={field.id}>
              {labelHitZone}
              <div
                title={fieldLabel}
                style={{
                  ...baseStyle,
                  cursor: viewingPast || isAnon ? "default" : "pointer",
                  textAlign: "left",
                  overflow: "hidden",
                  wordBreak: "break-word",
                }}
                onClick={() => {
                  if (!viewingPast && !isAnon) setEditingFieldId(field.id);
                }}
              >
                {isAnon ? "—" : toStr(value)}
              </div>
              {editingFieldId === field.id && (
                <PatientNamePopover
                  value={toStr(value)}
                  label={fieldLabel}
                  onCommitName={(name) => {
                    setEditingFieldId(null);
                    if (name !== (value ?? "")) onChange(name);
                  }}
                  onPickPatient={(patientId) => {
                    setEditingFieldId(null);
                    syncEntity(patientId).catch(() => {});
                    fire("case.setPatient", {
                      uuid: caseId,
                      patientUuid: patientId,
                    });
                  }}
                  onCreateNew={async (name) => {
                    setEditingFieldId(null);
                    const patientUuid = uuidv7();
                    const unitUuid = user?.unitIds?.[0] || "";
                    await fire("patient.create", {
                      uuid: patientUuid,
                      unitUuid,
                    });
                    if (name.trim())
                      await fire("patient.setName", {
                        uuid: patientUuid,
                        name: name.trim(),
                      });
                    fire("case.setPatient", { uuid: caseId, patientUuid });
                  }}
                  onCancel={() => setEditingFieldId(null)}
                  searchFn={async (term) => {
                    const currentPatientId = data?.patient?.id;
                    try {
                      const result = await rpc<PatientListResult>({
                        type: "listPatients",
                        search: term || undefined,
                        limit: 8,
                      });
                      return result.patients.filter(
                        (p) => p.id !== currentPatientId,
                      );
                    } catch {
                      // Offline fallback: search locally cached patients
                      const rows = term
                        ? await db
                            .select()
                            .from(s.patients)
                            .where(like(s.patients.name, `%${term}%`))
                        : await db.select().from(s.patients);
                      return rows
                        .filter((p) => p.id !== currentPatientId)
                        .slice(0, 8)
                        .map((p) => ({
                          id: p.id,
                          name: p.name,
                          dateOfBirth: p.dateOfBirth,
                          sex: p.sex,
                          bloodType: p.bloodType,
                          rhFactor: p.rhFactor,
                          allergyStatus: p.allergyStatus,
                          allergyDetails: p.allergyDetails,
                          militaryRank: p.militaryRank,
                          unitId: p.unitId,
                          unitName: "",
                          caseCount: 0,
                          updatedAt: p.updatedAt,
                        }));
                    }
                  }}
                  colors={colors}
                />
              )}
            </Fragment>
          );
        }

        default:
          return null;
      }
    });
  };

  // Memoize overlay per page — skip re-render when only zoom/dragging/historyOpen changes
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const overlay1 = useMemo(
    () => buildOverlay(1, pageState),
    [
      overlayData,
      optimistic,
      editingFieldId,
      viewingPast,
      debugMode,
      pageState,
      resolvedFields,
      colors,
      isAnon,
    ],
  );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const overlay2 = useMemo(
    () => buildOverlay(2, pageState),
    [
      overlayData,
      optimistic,
      editingFieldId,
      viewingPast,
      debugMode,
      pageState,
      resolvedFields,
      colors,
      isAnon,
    ],
  );


  // ─── Body diagram (trauma map) helpers ──────────────────────────────
  const rawTrauma = overlayData?.case?.traumaLocations;
  const savedTraumaMap: TraumaMap =
    rawTrauma &&
    !Array.isArray(rawTrauma) &&
    Array.isArray((rawTrauma as TraumaMap).burns)
      ? {
          ...(rawTrauma as TraumaMap),
          injuries: (rawTrauma as TraumaMap).injuries ?? [],
        }
      : { burns: [], traumas: [], injuries: [], arrows: [] };
  const [optimisticTraumaMap, setOptimisticTraumaMap] =
    useState<TraumaMap | null>(null);
  const traumaMap = optimisticTraumaMap ?? savedTraumaMap;
  // Clear optimistic value once the real data catches up
  useEffect(() => {
    if (optimisticTraumaMap) setOptimisticTraumaMap(null);
  }, [rawTrauma]);

  const handleExport = useCallback(async () => {
    if (!overlayData || exporting) return;
    setExporting(true);
    try {
      const bytes = await exportPdf(overlayData, resolvedFields, traumaMap, isAnon);
      const fileName = `${overlayData.case?.number || caseId}.pdf`;
      if (window.DataLab?.shareFile) {
        const b64: string = await new Promise((resolve) => {
          const reader = new FileReader();
          reader.onloadend = () =>
            resolve((reader.result as string).split(",")[1]);
          reader.readAsDataURL(new Blob([bytes.buffer as ArrayBuffer]));
        });
        window.DataLab.shareFile(b64, "application/pdf", fileName);
      } else {
        const blob = new Blob([bytes.buffer as ArrayBuffer], {
          type: "application/pdf",
        });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = fileName;
        a.click();
        URL.revokeObjectURL(url);
      }
    } catch (e) {
      console.error("PDF export failed:", e);
    } finally {
      setExporting(false);
    }
  }, [overlayData, resolvedFields, exporting, caseId, traumaMap]);

  const handleShare = useCallback(async () => {
    const url = window.location.href;
    if (window.DataLab?.shareText) {
      window.DataLab.shareText(url);
    } else if (navigator.share) {
      try {
        await navigator.share({ url });
      } catch {}
    } else {
      await navigator.clipboard.writeText(url);
    }
  }, []);

  const handleConfirmTrauma = useCallback(
    (updated: TraumaMap) => {
      setOptimisticTraumaMap(updated);
      // Diff burns
      const oldBurns = new Set(savedTraumaMap.burns);
      const newBurns = new Set(updated.burns);
      for (const z of updated.burns)
        if (!oldBurns.has(z))
          fire("case.setTraumaBurnItem", {
            uuid: caseId,
            zone: z,
            value: true,
          });
      for (const z of savedTraumaMap.burns)
        if (!newBurns.has(z))
          fire("case.setTraumaBurnItem", {
            uuid: caseId,
            zone: z,
            value: false,
          });
      // Diff traumas
      const oldTraumas = new Set(savedTraumaMap.traumas);
      const newTraumas = new Set(updated.traumas);
      for (const z of updated.traumas)
        if (!oldTraumas.has(z))
          fire("case.setTraumaZoneItem", {
            uuid: caseId,
            zone: z,
            value: true,
          });
      for (const z of savedTraumaMap.traumas)
        if (!newTraumas.has(z))
          fire("case.setTraumaZoneItem", {
            uuid: caseId,
            zone: z,
            value: false,
          });
      // Diff injuries
      const oldInjuryIds = new Set(savedTraumaMap.injuries.map((m) => m.id));
      const newInjuryIds = new Set(updated.injuries.map((m) => m.id));
      for (const m of updated.injuries)
        if (!oldInjuryIds.has(m.id))
          fire("case.addInjuryMark", {
            uuid: caseId,
            markId: m.id,
            x: m.x,
            y: m.y,
          });
      for (const m of savedTraumaMap.injuries)
        if (!newInjuryIds.has(m.id))
          fire("case.removeInjuryMark", { uuid: caseId, markId: m.id });
      setBodyDiagramOpen(false);
    },
    [caseId, fire, savedTraumaMap],
  );

  const handleCancelTrauma = useCallback(() => {
    setBodyDiagramOpen(false);
  }, []);

  // Memoized trauma indicators — only recalculate when traumaMap or page dimensions change
  const traumaIndicators = useMemo(() => {
    const ps = pageState;
    const nodes: React.ReactNode[] = [];
    if (traumaMap.burns.length) {
      const burnSet = new Set(traumaMap.burns);
      for (const z of PDF_ZONE_POSITIONS) {
        if (!burnSet.has(z.id)) continue;
        const cx = (z.xPct * ps.cssW) / 100;
        const cy = (z.yPct * ps.cssH) / 100;
        const r = Math.max(4, ps.cssW * 0.012);
        nodes.push(
          <div
            key={z.id}
            style={{
              position: "absolute",
              left: cx - r - 1,
              top: cy - r - 1,
              width: (r + 1) * 2,
              height: (r + 1) * 2,
              borderRadius: "50%",
              border: "2px solid #dc2626",
              pointerEvents: "none",
              zIndex: 2,
              boxSizing: "border-box",
            }}
          />,
        );
      }
    }
    if (traumaMap.traumas.length) {
      const traumaSet = new Set(traumaMap.traumas);
      for (const z of TRAUMA_CROSS_POSITIONS) {
        if (!traumaSet.has(z.id)) continue;
        const cx = (z.xPct * ps.cssW) / 100;
        const cy = (z.yPct * ps.cssH) / 100;
        const size = Math.max(6, ps.cssW * 0.016);
        nodes.push(
          <div
            key={z.id}
            style={{
              position: "absolute",
              left: cx - size / 2,
              top: cy - size / 2,
              width: size,
              height: size,
              pointerEvents: "none",
              zIndex: 2,
            }}
          >
            <CrossMark selected />
          </div>,
        );
      }
    }
    if (traumaMap.injuries?.length) {
      // injuries are stored as crop-relative %, convert to full-SVG pixel coords
      const CROP = { x: 425, y: 157, w: 154, h: 154 };
      for (const m of traumaMap.injuries) {
        const svgX = CROP.x + (m.x / 100) * CROP.w;
        const svgY = CROP.y + (m.y / 100) * CROP.h;
        const cx = (svgX / SVG_W) * ps.cssW;
        const cy = (svgY / SVG_H) * ps.cssH;
        const size = Math.max(6, ps.cssW * 0.016);
        nodes.push(
          <div
            key={`inj-${m.id}`}
            style={{
              position: "absolute",
              left: cx - size / 2,
              top: cy - size / 2,
              width: size,
              height: size,
              pointerEvents: "none",
              zIndex: 2,
            }}
          >
            <CrossMark selected />
          </div>,
        );
      }
    }
    return nodes.length ? nodes : null;
  }, [traumaMap, pageState]);

  // Help mode renders one `?` button per numbered subsection of the form
  // (e.g. 1.1, 1.2, ..., 4.1, ..., 27). Bounds are defined in HARDCODED_BOUNDS;
  // each entry is anchored to the printed section number on the form.
  const renderHelpOverlays = (pageNum: 1 | 2) => {
    if (!helpMode) return null;
    const BTN_W = 1.8;
    const BTN_H = BTN_W / ASPECT;
    return Object.entries(HARDCODED_BOUNDS[pageNum] ?? {}).map(
      ([sectionKey, bb]) => {
        const group = SECTION_TO_GROUP[sectionKey] ?? "identification";
        const palette = GROUP_COLOURS[group] ?? GROUP_COLOURS.identification;
        const isActive = helpSection === sectionKey;
        return (
          <button
            key={`help-btn-${sectionKey}`}
            type="button"
            title={t("formHelp.toggle")}
            onClick={(e) => {
              e.stopPropagation();
              setHelpSection(sectionKey);
            }}
            style={{
              position: "absolute",
              left: `${Math.max(0, bb.x - BTN_W * 0.9)}%`,
              top: `${Math.max(0, bb.y - BTN_H * 0.9)}%`,
              width: `${BTN_W}%`,
              height: `${BTN_H}%`,
              border: isActive ? `1px solid #fff` : "none",
              borderRadius: "50%",
              background: palette.base,
              color: "#fff",
              fontWeight: 700,
              fontSize: `${BTN_W * 0.55}cqi`,
              lineHeight: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              zIndex: 6,
              boxShadow: "0 1px 2px rgba(0,0,0,0.35)",
              padding: 0,
              opacity: isActive ? 1 : 0.92,
            }}
          >
            ?
          </button>
        );
      },
    );
  };

  const renderPage = (pageNum: 1 | 2) => (
    <div style={{ position: "relative", lineHeight: 0, zIndex: 0 }}>
      <MemoSvgBackground
        pageNum={pageNum}
        width={baseW}
        height={baseH}
        isDark={isDark}
      />
      {pageNum === 1 ? overlay1 : overlay2}
      {renderHelpOverlays(pageNum)}
      {/* Body diagram click zone + burn indicators — page 1 only */}
      {pageNum === 1 && (
        <>
          {/* Clickable zone covering both front and back body diagrams */}
          {!viewingPast && !helpMode && (
            <div
              onClick={() => setBodyDiagramOpen(true)}
              style={{
                position: "absolute",
                left: "72.57%",
                top: "18.89%",
                width: "25.53%",
                height: "17.94%",
                cursor: "pointer",
                zIndex: 3,
                background: debugMode ? "rgba(0,255,0,0.1)" : "transparent",
                border: debugMode ? "1px dashed green" : "none",
                boxSizing: "border-box",
              }}
              title="Body diagram — click to mark burns & trauma"
            />
          )}
          {traumaIndicators}
        </>
      )}
    </div>
  );

  return (
    <div
      ref={containerRef}
      style={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        position: "relative",
        background: colors.bg,
        overflow: "hidden",
      }}
    >
      {/* Loading overlay */}
      {(loading || !data || containerWidth === 0) && (
        <div
          style={{
            position: "absolute",
            inset: 0,
            zIndex: 20,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexDirection: "column",
            gap: 16,
            background: colors.bg,
            color: colors.textMuted,
          }}
        >
          <BioSpinner size={80} />
          {t("app.loading")}
        </div>
      )}

      {/* Top bar */}
      <div
        style={{
          flexShrink: 0,
          zIndex: 10,
          background: colors.bgAlt,
          borderBottom: `1px solid ${colors.border}`,
          padding: "0.25rem 0.5rem",
          display: "flex",
          flexDirection: "column",
          gap: "0.25rem",
        }}
      >
        {/* Case ID left, zoom + history right */}
        <div style={{ display: "flex", alignItems: "center", gap: "0.25rem" }}>
          {data?.case && (
            <span
              style={{
                fontWeight: 600,
                color: colors.text,
                fontSize: "0.85rem",
                marginRight: "0.5rem",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                minWidth: 0,
              }}
            >
              {data.case.number || caseId.slice(0, 8)}
              {data.patient && (
                <>
                  {" for "}
                  <a
                    href={`/patients/${data.patient.id}`}
                    onClick={(e) => {
                      e.preventDefault();
                      navigate(`/patients/${data.patient!.id}`);
                    }}
                    style={{
                      color: colors.primary,
                      textDecoration: "underline",
                      cursor: "pointer",
                      fontWeight: 500,
                    }}
                  >
                    {isAnon ? "—" : (data.patient.name?.trim() || data.patient.id.slice(0, 8))}
                  </a>
                  {` (${data.patientCaseCount})`}
                </>
              )}
            </span>
          )}
          <div style={{ flex: 1 }} />
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "0.25rem",
              flexShrink: 0,
              whiteSpace: "nowrap",
            }}
          >
            <button
              onClick={() => setZoom((z) => Math.max(minZoom, z - ZOOM_STEP))}
              style={styles.btnSmall}
            >
              −
            </button>
            <span
              style={{
                minWidth: "2.5rem",
                textAlign: "center",
                color: colors.text,
                fontSize: "0.75rem",
              }}
            >
              {Math.round(zoom * 100)}%
            </span>
            <button
              onClick={() => setZoom((z) => Math.min(MAX_ZOOM, z + ZOOM_STEP))}
              style={styles.btnSmall}
            >
              +
            </button>
            <button
              onClick={() => setZoom(1)}
              style={{ ...styles.btnSmall, marginLeft: "0.25rem" }}
            >
              {t("common.fit")}
            </button>
            <button
              onClick={() => {
                const next = !sideBySide;
                setSideBySide(next);
                localStorage.setItem("pdfForm.sideBySide", next ? "1" : "0");
              }}
              style={{
                ...styles.btnSmall,
                marginLeft: "0.25rem",
                background: sideBySide
                  ? colors.text
                  : styles.btnSmall.background,
                color: sideBySide ? colors.bg : styles.btnSmall.color,
                borderColor: sideBySide
                  ? colors.text
                  : styles.btnSmall.borderColor,
              }}
            >
              {sideBySide ? "⬌ 2pp" : "⬍ 1pp"}
            </button>
            <button
              onClick={() => {
                const next = !helpMode;
                setHelpMode(next);
                if (next) {
                  // Close any open editor when entering help mode so its
                  // popover doesn't sit on top of the help sidebar.
                  _setEditingFieldId(null);
                } else {
                  setHelpSection(null);
                }
              }}
              title={t("formHelp.toggle")}
              aria-label={t("formHelp.toggle")}
              style={{
                ...styles.btnSmall,
                marginLeft: "0.25rem",
                background: helpMode ? colors.text : styles.btnSmall.background,
                color: helpMode ? colors.bg : styles.btnSmall.color,
                borderColor: helpMode ? colors.text : styles.btnSmall.borderColor,
                fontWeight: 700,
              }}
            >
              ?
            </button>
            <button
              onClick={() => {
                const next = !historyOpen;
                setHistoryOpen(next);
                if (!next) {
                  setHistoryCursor(-1);
                  navigate(`/cases/${caseId}`, { replace: true });
                }
              }}
              style={{
                ...styles.btnSmall,
                marginLeft: "0.25rem",
                background: historyOpen
                  ? viewingPast
                    ? "#e74c3c"
                    : colors.text
                  : styles.btnSmall.background,
                color: historyOpen
                  ? viewingPast
                    ? "#fff"
                    : colors.bg
                  : styles.btnSmall.color,
                borderColor: historyOpen
                  ? viewingPast
                    ? "#e74c3c"
                    : colors.text
                  : styles.btnSmall.borderColor,
              }}
            >
              {viewingPast
                ? `⏱ ${t("history.viewingPast")}`
                : historyOpen
                  ? `✕ ${t("history.title")}`
                  : `⏱ ${t("history.title")}`}
            </button>
            <button
              onClick={handleShare}
              style={{ ...styles.btnSmall, marginLeft: "0.25rem" }}
            >
              {t("form.share")}
            </button>
            <button
              onClick={handleExport}
              disabled={exporting}
              style={{ ...styles.btnSmall, marginLeft: "0.25rem" }}
            >
              {exporting ? "..." : t("form.exportPdf")}
            </button>
          </div>
        </div>
      </div>

      {/* Main content + optional history sidebar — each pane scrolls independently */}
      <div style={{ display: "flex", flex: 1, minHeight: 0 }}>
        {/* Form pane — scrollable, with drag-to-scroll */}
        <div
          ref={formPaneRef}
          style={{
            flex: 1,
            overflow: "auto",
            minWidth: 0,
            cursor: dragging ? "grabbing" : "grab",
            touchAction: "none",
            overscrollBehavior: "contain",
          }}
        >
          {/* Attachments — inside the scrollable form pane so history sits beside them */}
          {overlayData && (
            <div
              style={{
                padding: "0.25rem 0.5rem",
                borderBottom: `1px solid ${colors.border}`,
              }}
            >
              <AttachmentsSection
                caseId={caseId}
                attachments={overlayData.attachments || []}
                readOnly={viewingPast}
              />
            </div>
          )}
          {/* Spacer: sets scroll dimensions; at least container-sized for centering */}
          <div
            ref={spacerRef}
            style={{
              width: zoomedW,
              minWidth: "100%",
              height: zoomedH,
              background: colors.bg,
            }}
          >
            {/* Pages: rendered at base size, CSS transform handles zoom (GPU-accelerated) */}
            <div
              ref={pagesRef}
              style={{
                display: "flex",
                flexDirection: sideBySide ? "row" : "column",
                alignItems: "flex-start",
                transform: `translate(${offsetX}px, ${offsetY}px) scale(${zoom})`,
                transformOrigin: "0 0",
                willChange: "transform",
              }}
            >
              {renderPage(1)}
              {(maxPage === undefined || maxPage >= 2) && renderPage(2)}
            </div>
          </div>
        </div>

        {/* History sidebar — independent scroll */}
        {historyOpen && (
          <div
            style={{
              width: "260px",
              minWidth: "220px",
              flexShrink: 0,
              overflowY: "auto",
              borderLeft: `1px solid ${colors.border}`,
              background: colors.bg,
              padding: "0.375rem",
              fontSize: "0.7rem",
              position: "relative",
            }}
          >
            {historyLoading && (
              <div
                style={{
                  position: "absolute",
                  inset: 0,
                  background: isDark
                    ? "rgba(0,0,0,0.4)"
                    : "rgba(255,255,255,0.5)",
                  zIndex: 5,
                }}
              />
            )}
            <div
              style={{
                fontWeight: 600,
                marginBottom: "0.5rem",
                color: colors.text,
                fontSize: "0.85rem",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <span>
                {historyLoading
                  ? t("history.title")
                  : t("history.total", {
                      count: String(historyData?.length ?? 0),
                    })}
              </span>
              {viewingPast && (
                <span
                  onClick={() => {
                    setHistoryCursor(-1);
                    navigate(`/cases/${caseId}`, { replace: true });
                  }}
                  style={{
                    fontSize: "0.7rem",
                    color: "#e74c3c",
                    cursor: "pointer",
                    fontWeight: 400,
                  }}
                >
                  ● {t("history.live")}
                </span>
              )}
            </div>
            {(!historyData || historyData.length === 0) && (
              <div style={{ color: colors.textMuted, fontStyle: "italic" }}>
                {t("history.noEvents")}
              </div>
            )}
            {historyData &&
              historyData.length > 0 &&
              historyDataAsc &&
              (() => {
                // Group events by date
                let lastDate = "";
                return historyData.map((evt) => {
                  const dateStr = fmtDate(evt.clientTimestamp);
                  const showDate = dateStr !== lastDate;
                  lastDate = dateStr;
                  const synced = evt.serverTimestamp > 0;
                  // Find this event's ASC index for history replay
                  const ascIdx = historyDataAsc.findIndex(
                    (e) => e.id === evt.id,
                  );
                  const isActive =
                    viewingPast &&
                    ((historyCursor === -1 &&
                      ascIdx === historyDataAsc.length - 1) ||
                      ascIdx === historyCursor);
                  const isFuture =
                    viewingPast && historyCursor >= 0 && ascIdx > historyCursor;
                  return (
                    <div key={evt.id}>
                      {showDate && (
                        <div
                          style={{
                            fontWeight: 600,
                            fontSize: "0.7rem",
                            color: colors.textMuted,
                            padding: "0.375rem 0 0.125rem",
                            borderBottom: `1px solid ${colors.border}`,
                            marginTop: "0.25rem",
                          }}
                        >
                          {dateStr}
                        </div>
                      )}
                      <div
                        onClick={() => {
                          if (ascIdx >= 0) goToEvent(evt, ascIdx);
                        }}
                        style={{
                          padding: "0.25rem 0.375rem",
                          borderBottom: `1px solid ${colors.border}`,
                          display: "flex",
                          flexDirection: "column",
                          gap: "1px",
                          cursor: "pointer",
                          opacity: isFuture ? 0.35 : 1,
                          background: isActive
                            ? isDark
                              ? "rgba(231,76,60,0.25)"
                              : "rgba(231,76,60,0.1)"
                            : undefined,
                          borderLeft: isActive
                            ? "3px solid #e74c3c"
                            : "3px solid transparent",
                        }}
                        title={`ID: ${evt.id}\nType: ${evt.type}\n${JSON.stringify(evt.payload, null, 2)}`}
                      >
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                          }}
                        >
                          <span
                            style={{
                              fontWeight: 500,
                              color: colors.text,
                              fontSize: "0.725rem",
                            }}
                          >
                            {commandLabel(evt.type)}
                          </span>
                          <span
                            style={{
                              fontSize: "0.65rem",
                              color: colors.textMuted,
                              fontFamily: "monospace",
                              whiteSpace: "nowrap",
                              marginLeft: "0.5rem",
                            }}
                          >
                            {fmtTime(evt.clientTimestamp)}
                            {!synced && (
                              <span
                                style={{ color: "#e67e22", marginLeft: "3px" }}
                                title="Not synced"
                              >
                                ●
                              </span>
                            )}
                          </span>
                        </div>
                        <div
                          style={{
                            fontSize: "0.65rem",
                            color: colors.textMuted,
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          {formatValue(evt.payload)}
                        </div>
                        {userMap && userMap[evt.userId] && (
                          <div
                            style={{
                              fontSize: "0.6rem",
                              color: colors.textMuted,
                              fontStyle: "italic",
                              opacity: 0.7,
                            }}
                          >
                            {userMap[evt.userId]}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                });
              })()}
          </div>
        )}

      </div>

      {/* Help popup — modal over the form, mobile-first */}
      {helpMode && helpSection !== null && (() => {
        const helpContent = getFieldHelpContent(helpSection, resolvedFields, t);
        const palette =
          GROUP_COLOURS[helpContent.group] ?? GROUP_COLOURS.identification;
        return (
          <div
            onClick={() => setHelpSection(null)}
            style={{
              ...POPOVER_BACKDROP,
              alignItems: "flex-end",
              justifyContent: "center",
              background: "rgba(0,0,0,0.45)",
            }}
          >
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                background: colors.bg,
                color: colors.text,
                width: "100%",
                maxWidth: "560px",
                maxHeight: "85vh",
                overflowY: "auto",
                borderTopLeftRadius: "14px",
                borderTopRightRadius: "14px",
                padding: "1rem 1rem 1.25rem",
                boxShadow: "0 -4px 20px rgba(0,0,0,0.25)",
                borderTop: `4px solid ${palette.base}`,
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                  gap: "0.75rem",
                  marginBottom: "0.5rem",
                }}
              >
                <span
                  style={{
                    fontWeight: 700,
                    fontSize: "1.05rem",
                    color: palette.base,
                    lineHeight: 1.25,
                  }}
                >
                  {helpContent.title}
                </span>
                <button
                  aria-label={t("formHelp.exit")}
                  onClick={() => setHelpSection(null)}
                  style={{
                    border: "none",
                    background: "transparent",
                    color: colors.textMuted,
                    fontSize: "1.4rem",
                    lineHeight: 1,
                    cursor: "pointer",
                    padding: "0 0.25rem",
                  }}
                >
                  ×
                </button>
              </div>
              <div
                style={{
                  whiteSpace: "pre-wrap",
                  lineHeight: 1.5,
                  fontSize: "0.95rem",
                }}
              >
                {helpContent.body}
              </div>
            </div>
          </div>
        );
      })()}

      {/* Body diagram popover */}
      {bodyDiagramOpen && (
        <BodyDiagramPopover
          traumaMap={savedTraumaMap}
          onConfirm={handleConfirmTrauma}
          onCancel={handleCancelTrauma}
          colors={colors}
          isDark={isDark}
        />
      )}
    </div>
  );
}
